/* We Are Jolies — B2B Order Pad
 * <order-pad>            per-product size/qty pad. Standalone (PDP + quick-buy):
 *                        own footer + one /cart/add.js add-all. Aggregate (collection
 *                        order grid): no footer, feeds an <order-pad-collection>.
 * <order-pad-collection> aggregates every pad in the collection order grid:
 *                        sticky bar (running units + HT total), expandable detail
 *                        table, one add-all across all products -> cart drawer.
 *                        Internal Saisie-directe <-> Repliées mode toggle (CD-provided). */
(function () {
  'use strict';

  if (customElements.get('order-pad')) return;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    var value;
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount': value = defaultTo(cents, 2); break;
      case 'amount_no_decimals': value = defaultTo(cents, 0); break;
      case 'amount_with_comma_separator': value = defaultTo(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = defaultTo(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = defaultTo(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = defaultTo(cents, 0, ' ', ''); break;
      default: value = defaultTo(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Exposed so `b2b-ship-quote.js` formats money through THIS function rather than carrying its
  // own copy. Two sources of truth for one value is this engagement's recurring defect (D119.2):
  // a duplicated formatter would drift the moment the shop's money format changed, and the losing
  // copy would be invisible until someone noticed a total punctuated differently from the row
  // above it.
  window.WAJ = window.WAJ || {};
  window.WAJ.formatMoney = formatMoney;

  // Mirror Focal's own add sequence: refresh mini-cart + open it.
  //
  // 🔴 A PARTIAL ADD OPENS THE DRAWER EXACTLY LIKE ANY OTHER ADD — operator ruling 2026-08-07,
  // reversing a DERIVED change made earlier the same day (D22.2, withdrawn). That change suppressed
  // the drawer on a partial add so nothing covered the report. It was unnecessary: the report now
  // sits in the post-add feedback slot, which is the SAME slot « ✓ Ajouté au panier » has always
  // used while the drawer opens over it. There was never a conflict to design around — and a
  // partial add is a successful add, so it must not behave differently from one.
  //
  // NOTHING about what reaches the cart depended on this, then or now: the add itself is untouched.
  function refreshCartAndOpen(cartUrl, addedSections) {
    return fetch(cartUrl + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        if (addedSections) cart.sections = addedSections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: true } }));
        return cart;
      });
  }

  // 🔴 46e — `aria-disabled` IS THE WHOLE STATE. The bubble is ours (b2b-stock-ceiling.js reads
  // `data-b2b-ceiling` + `data-ceiling-label` off the button and renders one shared fixed node), so
  // there is nothing else to toggle. It is a function rather than an inline set because the first
  // build had TWO flags to keep in step and got it wrong; keeping the seam named means a third can
  // never be added in one place only.
  function setCeilingState(button, atCeiling) {
    if (!button) return;
    var was = button.getAttribute('aria-disabled') === 'true';
    button.setAttribute('aria-disabled', atCeiling ? 'true' : 'false');
    // Only on a real transition, and only when there is a bubble to care: reaching the ceiling is
    // done by clicking the +, so the cursor is already on it and no hover event will fire again.
    // b2b-stock-ceiling.js listens and shows the bubble if the pointer is sitting there.
    if (was !== atCeiling && button.hasAttribute('data-b2b-ceiling')) {
      button.dispatchEvent(new CustomEvent('b2b:ceiling', { bubbles: true }));
    }
  }

  // ── 42c / CD-124 — « un ajout partiel n'est pas une erreur » ────────────────────────────────
  // CD's ruling, in three parts: everything that CAN go in goes in · the BAR says nothing · the
  // report lives AT THE CART, naming every affected line.
  //
  // 🔴 THE CODEBASE'S STATED PREMISE WAS WRONG, AND THE FIX IS SMALLER BECAUSE OF IT.
  // This file previously asserted, in a comment: « /cart/add.js is ATOMIC over items[] — a single
  // out-of-stock line rejects the whole request and nothing is added. » Round-tripped against the
  // live store (2026-08-02): it is NOT. A three-line add with one line wildly over stock returned
  // HTTP 422 **and added all three lines**, clamping the over-stock one to the quantity actually
  // available (2, 2, 73 requested 2, 2, 99999).
  //
  // So « tout ce qui peut entrer entre » is already the PLATFORM's behaviour. What was broken was
  // only our reading of it: we saw the 422, called it a total failure, showed an error and told the
  // buyer to retry — while their lines were sitting in the cart. No per-line retry loop is needed;
  // an earlier draft of this change built one and it was pure waste.
  //
  // What we do need is to know WHICH lines were clamped, so the report can name them. That is a
  // cart diff, not a message parse: snapshot the per-variant quantities before the add, compare
  // after, and any line that landed short of (before + requested) was clamped. Exact, and it does
  // not depend on the wording or the status code.
  var PARTIAL_KEY = 'waj_b2b_partial_add';

  function cartQuantitiesByVariant(cartUrl) {
    return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var map = {}, total = 0;
        (cart.items || []).forEach(function (i) {
          map[i.variant_id] = (map[i.variant_id] || 0) + i.quantity;
          total += i.quantity;
        });
        return { map: map, total: total };
      })
      .catch(function () { return { map: {}, total: -1 }; });
  }

  /* 🔴 THE RECOVERY PATH, shared by both ordering surfaces (2026-08-07, bisected 2026-08-29).
     `/cart/add.js` handles a stock condition in TWO ways: it CLAMPS and partially adds when a line
     merely asks for more than is available, and it REJECTS THE WHOLE PAYLOAD when a line cannot
     move at all.
     🔴 « CANNOT MOVE AT ALL » MEANS **SOLD OUT** — measured against the live endpoint 2026-08-30,
     because the distinction was being stated loosely in several comments here. A line whose CEILING
     IS ALREADY IN THE CART does NOT reject the payload: it clamps to zero and everything else
     lands, exactly like an over-ask. Only genuine sold-out stock produces the wholesale 422 with
     nothing added. The code is indifferent — it decides on a cart diff, never on the reason — but a
     comment that is wrong about the platform is what the next reader builds on.
     ✅ The whole recovery path was reproduced live on 2026-08-30 (D305.3): an 8-line pad with one
     sold-out size landed all 7 live sizes, named the dead one, in 7 requests. In the second case a single dead size destroys an entire wholesale order — so the
     lines have to be re-sent in smaller pieces, which is the only way the others can still get in.
     Sequential on purpose: the cart is ONE server-side resource and concurrent adds race.

     🔴 WHY THIS IS A BISECT AND NOT A LOOP, AND WHY THE STATUS CODE CANNOT DRIVE IT (D287.4).
     The first version re-sent ONE LINE AT A TIME: 125 sequential round-trips to route around a
     single dead size, which is the whole of the observed add-all slowness. The obvious fix — split
     the payload in half and recurse into whichever half failed — is UNSAFE here, because those two
     behaviours above are not distinguishable from the response: a 422 on a chunk can mean « nothing
     moved » OR « some of it clamped in ». Recursing into a chunk that partially landed re-sends
     units that are already in the cart, i.e. it DOUBLE-ADDS a wholesale order.
     So the recursion is decided by a CART DIFF, never by the status code: snapshot per-variant
     quantities before the chunk, read them back after a failure, and recurse only into the lines
     that did not move AT ALL. A line that moved fully is landed; a line that moved short was
     clamped and is terminal (re-sending it is exactly the double-add); a line that did not move is
     the only thing worth splitting. One dead line in 125 costs ~2·log₂(n) requests instead of 125.

     🔴 A REFUSAL AND A TRANSPORT FAILURE ARE NOT THE SAME VERDICT, and the first version lumped
     them. Both ended in `refused`, and `refused` now decides whether a line LEAVES the buyer's
     selection (2026-08-29). Conflating them would mean a dropped connection silently eats typed
     quantities — a wholesale buyer's worst outcome. A non-ok HTTP response is Shopify saying « this
     line cannot move »: a stock verdict, and the line has nothing left to do in the selection. A
     thrown fetch is the network, which says nothing about stock, so that line is DEFERRED and
     stays. A cart read that itself fails is the same class of ignorance: everything still unplaced
     is DEFERRED rather than guessed at, because the alternative is deciding a stock question from
     no evidence. The returned shape is unchanged, so both call sites are untouched by this. */
  function addItemsRecover(addUrl, cartUrl, items, alreadyRejected) {
    var landed = [], refused = [], deferred = [];

    function post(chunk) {
      return fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: chunk })
      })
        .then(function (r) { return { ok: r.ok }; })
        .catch(function () { return { ok: false, thrown: true }; });
    }

    function run(chunk) {
      /* A single line needs no diff: whatever happened to it, it is terminal — it either went in,
         or it is a stock verdict, or the network dropped. Nothing left to split. */
      if (chunk.length === 1) {
        return post(chunk).then(function (res) {
          if (res.ok) landed.push(chunk[0].id);
          else if (res.thrown) deferred.push(chunk[0].id);
          else refused.push(chunk[0].id);
        });
      }
      return cartQuantitiesByVariant(cartUrl).then(function (before) {
        return post(chunk).then(function (res) {
          if (res.ok) { chunk.forEach(function (it) { landed.push(it.id); }); return; }
          return cartQuantitiesByVariant(cartUrl).then(function (after) {
            /* No trustworthy diff ⇒ no recursion. Splitting on a guess is the double-add. */
            if (before.total < 0 || after.total < 0) {
              chunk.forEach(function (it) { deferred.push(it.id); });
              return;
            }
            var stuck = [];
            chunk.forEach(function (it) {
              var moved = (after.map[it.id] || 0) - (before.map[it.id] || 0);
              if (moved >= it.quantity) landed.push(it.id);
              else if (res.thrown) deferred.push(it.id);   // transport: never a stock verdict
              else if (moved > 0) refused.push(it.id);     // clamped — terminal, NEVER re-sent
              else stuck.push(it);
            });
            if (!stuck.length) return;
            if (res.thrown) return;
            var mid = Math.ceil(stuck.length / 2);
            return run(stuck.slice(0, mid)).then(function () { return run(stuck.slice(mid)); });
          });
        });
      });
    }

    /* 🔴 DO NOT RE-POST THE PAYLOAD THAT JUST FAILED (2026-08-30, seen in a live trace).
       Both callers reach here having already sent the whole set and confirmed, by cart diff, that
       nothing landed. Starting the recursion with `run(items)` posted that identical payload a
       second time — guaranteed to fail, and it is the SLOWEST request of the run, since it is the
       largest. Measured on an 8-line pad with one dead size: 8 requests, of which the second was
       pure waste. Split straight away instead. */
    if (alreadyRejected && items.length > 1) {
      var mid = Math.ceil(items.length / 2);
      return run(items.slice(0, mid))
        .then(function () { return run(items.slice(mid)); })
        .then(function () { return { landed: landed, refused: refused, deferred: deferred }; });
    }

    return run(items).then(function () {
      return { landed: landed, refused: refused, deferred: deferred };
    });
  }

  // 🔴 46f DOES NOT SUPERSEDE 42c — THEY ARE TWO DIFFERENT CASES, AND CONFLATING THEM WAS A REAL
  // ERROR (corrected 2026-08-07 on operator challenge, confirmed against the frame).
  //
  // 46e/46f §7, verbatim: « rien n'a bougé = cas B, quelque chose a bougé = cas A (42c fonctionne,
  // INCHANGÉ) ». And on why case B's band lives on the ordering surface: « un rapport porté par les
  // lignes déjà au panier exigerait d'ouvrir le tiroir SUR UN NON-ÉVÉNEMENT (rien n'a été ajouté —
  // 43c a déjà jugé que rien ne s'ouvre alors) ».
  //
  // So the two reports have different homes for a reason that is about the DRAWER:
  //
  //   CASE A — partial add, something landed. 42c, untouched. The drawer OPENS (an add happened),
  //            so the report belongs AT THE CART, where the buyer is being taken and where the
  //            landed lines now are. `surface: 'cart'`.
  //   CASE B — the race: a 422 and nothing moved at all. No add happened, so per 43c NO DRAWER
  //            OPENS — and that is precisely why the band can live on the ordering surface: there
  //            is nothing about to cover it. `surface: 'pad'` / `'panel'`.
  //
  // Putting case A's report on the ordering surface — the first build's mistake — hid it behind the
  // drawer that a successful add always opens, on mobile behind a full-width one. The placement was
  // never the ruling; the CASE was.
  //
  // The old bare-array shape is still read (a report saved by an earlier build can be in
  // sessionStorage right now) and treated as the cart's, which is where 42c always put it.
  function savePartialReport(lines, surface) {
    try {
      if (lines && lines.length) {
        sessionStorage.setItem(PARTIAL_KEY, JSON.stringify({ lines: lines, surface: surface || 'pad' }));
      } else sessionStorage.removeItem(PARTIAL_KEY);
    } catch (e) {}
  }

  function readPartialReport() {
    try {
      var raw = sessionStorage.getItem(PARTIAL_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return { lines: parsed, surface: 'cart' };
      return (parsed && parsed.lines && parsed.lines.length) ? parsed : null;
    } catch (e) { return null; }
  }

  // 🔴 The report is held in sessionStorage, NOT in a cart attribute. A cart attribute would render
  // server-side and survive every re-render for free — genuinely tempting — but cart attributes are
  // carried onto the ORDER, so a report left uncleared would reach a real order as a note. Client
  // storage cannot touch order data, and that is worth the re-injection cost.
  function renderPartialBanner(root, copy, surface) {
    if (!root) return;
    var report = readPartialReport();
    var existing = root.querySelector('.b2bpa');
    if (existing) existing.remove();
    if (!report) return;
    var lines = report.lines;

    var box = document.createElement('div');
    // The modifier carries the SPACING, which differs by slot: in the cart the band precedes the
    // yellow « Sélection en cours » reminder and owes it the same 12px the reminder owes the
    // products; below the CTA it follows a button and owes the gap above itself instead.
    box.className = 'b2bpa b2bpa--' + (surface === 'cart' || !surface ? 'cart' : 'order');
    box.setAttribute('role', 'status');

    var head = document.createElement('div');
    head.className = 'b2bpa__head';

    var txt = document.createElement('div');
    txt.className = 'b2bpa__text';
    var t = document.createElement('div');
    t.className = 'b2bpa__title';
    t.textContent = copy.title;
    var b = document.createElement('div');
    b.className = 'b2bpa__body';
    b.textContent = copy.body;
    txt.appendChild(t);
    txt.appendChild(b);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'b2bpa__close';
    close.setAttribute('title', copy.close);
    close.setAttribute('aria-label', copy.close);
    close.textContent = '✕';
    // « ✕ de bandeau : se ferme, non persistant. »
    close.addEventListener('click', function () { savePartialReport(null); box.remove(); });

    head.appendChild(txt);
    head.appendChild(close);

    var list = document.createElement('div');
    list.className = 'b2bpa__lines';
    // 🔴 « les lignes nommées — toutes ». Naming every affected line IS the feature: the old
    // message named none of them, across a selection of dozens.
    lines.forEach(function (l) {
      var s = document.createElement('span');
      s.className = 'b2bpa__line';
      s.textContent = l;
      list.appendChild(s);
    });

    box.appendChild(head);
    box.appendChild(list);

    // 🔴 CASE B GOES WHERE THE « ✓ Ajouté au panier » CONFIRMATION GOES — below the CTA, in the
    // surface's own post-add feedback slot. Operator ruling 2026-08-07, overriding 46f's drawing:
    // « its location is still wrong … should go where green atc confirmation goes and not at top of
    // order pad like CD had wrongly done due to not drawing canonical order pad on 46f ». The frame
    // composes a pad this theme does not have (banner under the title, table beneath), so its
    // placement is not transferable — the INTENT (the ordering surface answers where the gesture
    // was made) is, and this slot is where that surface already answers.
    //
    // Case A keeps 42c's own slot: the head of the cart list, above the yellow reminder.
    var anchor = root.querySelector('[data-op-feedback], [data-oc-feedback]');
    if (anchor && anchor.parentNode) { anchor.parentNode.insertBefore(box, anchor); return; }
    root.insertBefore(box, root.firstChild);
  }

  // One copy source, read off the globally-rendered drawer, so every surface says the same thing.
  //
  // 🔴 TWO PAIRS, PICKED BY CASE. « Certains articles n'ont pas été ajoutés / Leur quantité maximale
  // est déjà dans votre panier » is a PARTIAL-add sentence: it presumes some lines went in and
  // explains why the rest did not. In case B nothing went in at all, so both halves are false —
  // reported by the operator against a live screen. Case B gets its own pair.
  function partialCopy(surface) {
    var el = document.querySelector('#mini-cart');
    var race = surface === 'pad';
    return {
      title: (el && el.getAttribute(race ? 'data-race-title' : 'data-partial-title')) || '',
      body: (el && el.getAttribute(race ? 'data-race-body' : 'data-partial-body')) || '',
      close: (el && el.getAttribute('data-partial-close')) || 'Fermer'
    };
  }

  // 🔴 THE BANNER MUST BE SELF-HEALING, NOT INJECTED ONCE.
  // First attempt injected it in the `.then()` after the cart refresh and it never appeared: that
  // call dispatches `cart:refresh`, and Focal re-renders sections ASYNCHRONOUSLY afterwards,
  // replacing the host and wiping the banner. Timing it with a delay would be a guess.
  //
  // Instead, one observer keeps the invariant « a report exists ⇒ the band is on screen », so it
  // survives Focal's re-render and every later one. The guard is what stops the observer
  // re-triggering on its own insertion.
  //
  // The host follows the CASE (see above), never a placement preference.
  //
  //   'cart'  → 42c's own two mounts: the drawer's list and the cart page's. The drawer is opening
  //             anyway; this is where the buyer is going.
  //   'pad'   → case B on the PDP pad: the band, in the pad's own post-add feedback slot.
  //
  // 🔴 THERE IS NO BAND ON THE SELECTION SURFACE. Case B from the bar speaks in CD's pink PILL
  // (22d state E, `.ocg__bar-err`) instead — operator ruling 2026-08-07. The bar is a one-line
  // sticky strip; a titled band with a list of named lines does not belong in it, and the rows on
  // that surface carry the detail themselves by converting in place.
  function partialHosts(surface) {
    if (surface === 'pad') {
      return Array.prototype.slice.call(document.querySelectorAll('order-pad:not([data-aggregate])'));
    }
    return [document.querySelector('#mini-cart-form'), document.querySelector('.b2bc__lines')].filter(Boolean);
  }

  // 🔴 THE REPORT MUST NOT OUTLIVE THE DRAWER VISIT THAT SHOWED IT (operator, 2026-08-27).
  // The band was only ever cleared by its own ✕, and the self-healing observer faithfully re-injected
  // it on every later cart open — so a buyer who closed the drawer instead of the band met the same
  // « certains articles n'ont pas été ajoutés » again, about an add they had already dealt with.
  //
  // Closing the drawer is a dismissal too. This arms a one-shot that waits for the drawer to be seen
  // OPEN and then HIDDEN, and clears the report at that transition.
  //
  // Deliberately NOT `whenDrawerGone`: that helper fires `done()` on a 1200 ms deadline as well as on
  // hidden, which is right for unblocking a click but would here delete the report ~1.2 s after it
  // rendered, while the buyer is still reading it. We need the open→closed EDGE, not a timeout.
  // Polling matches the house idiom (visibility is CSS-driven, so there is no event to bind), and the
  // loop self-terminates on the edge or as soon as the report is gone by any other route.
  function armPartialClearOnDrawerClose() {
    var drawer = document.querySelector('#mini-cart');
    if (!drawer || drawer.__b2bpaArmed) return;
    drawer.__b2bpaArmed = true;
    var seenOpen = false;
    var tick = function () {
      if (!readPartialReport()) { drawer.__b2bpaArmed = false; return; }
      var visible = window.getComputedStyle(drawer).visibility !== 'hidden';
      if (visible) seenOpen = true;
      else if (seenOpen) {
        savePartialReport(null);
        // Remove the rendered band here too rather than waiting for the observer's next mutation:
        // closing the drawer may be the last DOM change for a while, and a band that outlives its
        // own report is the whole defect.
        Array.prototype.forEach.call(document.querySelectorAll('.b2bpa'), function (el) { el.remove(); });
        drawer.__b2bpaArmed = false;
        return;
      }
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  function ensurePartialBanner() {
    var report = readPartialReport();
    // 🔴 THE INVARIANT IS TWO-DIRECTIONAL — « a report exists ⇔ the band is on screen ».
    // It used to be enforced in one direction only: this function injected the band when a report
    // existed and returned silently when it did not, and NOTHING ever removed a band already in
    // the DOM. `armPartialClearOnDrawerClose` (2026-08-27) cleared the sessionStorage report on the
    // drawer's open→closed edge and was believed to have closed the loop — it did not: the band it
    // was clearing is a DOM node in `#mini-cart-form` / `.b2bc__lines`, and clearing the report
    // never touched it. So a buyer who closed the drawer instead of the ✕ reopened it onto the same
    // « certains articles n'ont pas été ajoutés » band, exactly as before the fix.
    // The ✕ path worked only because its own handler calls `box.remove()` explicitly, which is why
    // the drawer path read as built when it was not. (Operator, 2026-08-29.)
    if (!report) {
      Array.prototype.forEach.call(document.querySelectorAll('.b2bpa'), function (el) { el.remove(); });
      return;
    }
    var copy = partialCopy(report.surface);
    partialHosts(report.surface).forEach(function (host) {
      if (!host.querySelector('.b2bpa')) renderPartialBanner(host, copy, report.surface);
    });
    if (report.surface === 'cart' || !report.surface) armPartialClearOnDrawerClose();
  }

  // ── 40a §2.5 — « SÉLECTION EN COURS », the yellow reminder ──────────────────────────────────
  // « Le rappel prend la place de la jauge en tête de liste et DÉFILE AVEC ELLE » — « il informe,
  // il ne pilote pas ; le footer, lui, doit rester sous le pouce. » So it is a plain first child of
  // the cart list, never sticky, never in the footer.
  //
  // 🔴 THE SOURCE IS THE PERSISTED SELECTION, NOT A NEW ONE. `waj-b2b-selection` in localStorage is
  // the basket <order-pad-collection> already writes (see loadSelection / saveSelection below); this
  // reads the SAME key and invents nothing. Read here rather than off the element because the drawer
  // is global and the element is not: on a PDP or on the cart page there is no
  // <order-pad-collection> in the DOM at all, and that is exactly where a standing selection most
  // needs restating.
  //
  // When the element IS present its own `totals()` is preferred — it applies 43b's
  // cap = stock − cart, so the reminder can never state a larger figure than the bar 40px away.
  // The storage-only fallback has no cart map to subtract from and therefore reports the raw
  // selection; the two agree except for lines the cart has since caught up with.
  var SELECTION_KEY = 'waj-b2b-selection';

  // 🔴 `el` DEFAULTS to the drawer, but the caller can name its own source — and on the cart page it
  // must. `layout/theme.liquid` wraps `{% section 'mini-cart' %}` in
  // `{% unless settings.cart_type == 'page' or request.page_type == 'cart' %}`: Focal does not
  // render a cart drawer on the cart page, so `#mini-cart` is absent there and every string this
  // returns was empty. That is what killed 26c — see `ensurePageSelectionReminder`.
  function selectionCopy(source) {
    var el = source || document.querySelector('#mini-cart');
    function attr(n, d) { return (el && el.getAttribute(n)) || d; }
    return {
      titleOne: attr('data-sel-title-one', ''),
      titleOther: attr('data-sel-title-other', ''),
      body: attr('data-sel-body', ''),
      action: attr('data-sel-action', ''),
      money: attr('data-money-format', '${{amount}}')
    };
  }

  // Defensive for the same reason loadSelection is: storage throws on access in Safari private
  // mode, and its contents are user-editable. A malformed payload degrades to « no selection »,
  // which renders nothing — never a half-written figure.
  function selectionTotals() {
    var ocg = document.querySelector('order-pad-collection');
    if (ocg && typeof ocg.totals === 'function' && ocg.selection) {
      var t = ocg.totals();
      var lines = 0, sel = ocg.selection;
      Object.keys(sel).forEach(function (id) {
        if (ocg.committableQty(id, sel[id]) > 0) lines++;
      });
      return { lines: lines, cents: t.cents };
    }
    try {
      var raw = window.localStorage.getItem(SELECTION_KEY);
      if (!raw) return { lines: 0, cents: 0 };
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { lines: 0, cents: 0 };
      var n = 0, cents = 0;
      Object.keys(parsed).forEach(function (id) {
        var e = parsed[id];
        if (!e || typeof e !== 'object') return;
        var qty = parseInt(e.qty, 10), price = parseInt(e.price, 10);
        if (!(qty > 0) || isNaN(price)) return;
        n++;
        cents += qty * price;
      });
      return { lines: n, cents: cents };
    } catch (e) { return { lines: 0, cents: 0 }; }
  }

  // Runs `done` once the cart drawer is no longer part of the viewport's hit stack — i.e. once
  // Focal's close transition has finished and `visibility` has flipped to `hidden`. Condition-based
  // and self-terminating, with a deadline so a drawer that never transitions (reduced motion, a
  // theme override, an interrupted animation) still lets the panel open rather than swallowing the
  // buyer's click. Callers: « reprendre la sélection », whose whole defect this is.
  function whenDrawerGone(drawer, done) {
    if (!drawer) { done(); return; }
    var deadline = Date.now() + 1200;
    var tick = function () {
      if (window.getComputedStyle(drawer).visibility === 'hidden' || Date.now() > deadline) {
        done();
        return;
      }
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  // `opts.modifier` is the ONLY thing that differs between 40a (drawer) and 26c (cart page): same
  // basket, same reader, same copy, same DOM — two drawings, so two sets of metrics, carried on a
  // modifier class rather than by duplicating this function. `opts.href` is 26c's fallback
  // destination for the action (see the click handler below).
  function renderSelectionReminder(root, copy, totals, opts) {
    if (!root) return;
    opts = opts || {};
    var existing = root.querySelector('.b2bsel');
    if (existing) existing.remove();
    // « Absent quand la sélection est vide » — no empty state, no zero figure.
    if (!totals.lines) return;

    var box = document.createElement('div');
    box.className = 'b2bsel' + (opts.modifier ? ' ' + opts.modifier : '');
    // The signature is what lets `ensureSelectionReminder` be BOTH idempotent (so the observer does
    // not re-trigger on its own insertion) and FRESH (so a selection edited between two drawer
    // openings is not restated with stale figures). A blind "already present ⇒ skip" guard buys the
    // first and loses the second.
    box.setAttribute('data-b2bsel-sig', totals.lines + ':' + totals.cents);

    var title = document.createElement('div');
    title.className = 'b2bsel__title';
    var tpl = totals.lines > 1 ? copy.titleOther : copy.titleOne;
    title.textContent = tpl
      .replace('{n}', String(totals.lines))
      .replace('{total}', formatMoney(totals.cents, copy.money));

    var sub = document.createElement('div');
    sub.className = 'b2bsel__sub';
    var body = document.createElement('span');
    body.className = 'b2bsel__body';
    body.textContent = copy.body;
    var action = document.createElement('button');
    action.type = 'button';
    action.className = 'b2bsel__action';
    action.textContent = copy.action;
    // « Reprendre la sélection » = leave the cart and go back to composing it. The drawer closes via
    // Focal's own close control, and the selection panel re-opens if this page carries one.
    // 🔴 Where neither exists — the CART PAGE, which has no <order-pad-collection> at all — 40a and
    // 26c both draw the affordance and neither names a destination. Rather than ship a control that
    // visibly does nothing, 26c passes `opts.href`: the merchant's OWN « continuer mes achats »
    // target, read off the hook in main-cart.liquid. That is a setting the merchant already
    // maintains for the same intent, not a URL invented in JS. Absent the setting, the old
    // do-nothing behaviour stands rather than a guess.
    //
    // 🔴 …AND IT WAITS FOR THE DRAWER TO ACTUALLY BE GONE BEFORE OPENING — fixed 2026-08-04.
    // As built, this closed the drawer and opened the panel in the SAME tick, and the panel came up
    // at a fifth of the height it gets from « Afficher ». Focal's drawer takes 600ms to close and
    // stays HIT-TESTABLE for all of it: `.drawer__overlay` is `position: fixed; top: 0;
    // height: 100vh` (and at 390 full bleed the drawer itself is), and `visibility` only flips to
    // `hidden` when the transition ENDS. `sizePanel()` probes the top of the viewport for sticky
    // chrome that would clip the panel, so it measured the closing overlay: `topOccupied` became
    // the full viewport height, `available` went negative, and the rows cap clamped to its 104px
    // floor — where it then STAYED, because nothing re-measures once the panel is open.
    // Measured on the dev theme 2026-08-04, 1440×900, scrollY 3000, bar pinned at top 828:
    //     via « Afficher »              → cap 697px, ground 442px
    //     via « reprendre la sélection » → cap 104px, ground 104px
    //     the probe's first hit: SPAN.drawer__overlay, position fixed, top 0, bottom 900
    // So the panel now opens only once the drawer has left the hit stack, and that is OBSERVED per
    // frame rather than waited out: `visibility: hidden` is exactly the state that removes it from
    // `elementsFromPoint`, and a fixed delay could only ever be lucky against a transition whose
    // duration we are not allowed to assume (the trap the old 260ms scroll-to-line hit).
    action.addEventListener('click', function () {
      var drawer = document.querySelector('#mini-cart');
      var closer = drawer && drawer.querySelector('[data-action="close"]');
      if (closer) closer.click();
      var ocg = document.querySelector('order-pad-collection');
      if (ocg && typeof ocg.setPanel === 'function') {
        whenDrawerGone(drawer, function () { ocg.setPanel(true); });
        return;
      }
      if (opts.href) window.location.href = opts.href;
    });
    sub.appendChild(body);
    sub.appendChild(action);

    box.appendChild(title);
    box.appendChild(sub);

    // 🔴 DETERMINISTIC ORDER AGAINST 42c's PARTIAL-ADD BANNER, which 42c gives the SAME slot.
    // Chosen order: the partial-add report on TOP, the reminder beneath it. The report is the
    // transient account of what the commit just did and has a ✕ that dismisses it; the reminder is
    // standing state that outlives the visit. Putting the transient first is also what keeps the
    // two insertion paths order-independent: `.b2bpa` always inserts at firstChild, and this one
    // always inserts AFTER any `.b2bpa`, so whichever renders second lands correctly.
    var pa = root.querySelector('.b2bpa');
    root.insertBefore(box, pa ? pa.nextSibling : root.firstChild);
  }

  function ensureSelectionReminder() {
    var copy = selectionCopy();
    if (!copy.titleOther && !copy.titleOne) return; // non-b2b drawer: nothing stamped, nothing to do
    var totals = selectionTotals();
    var sig = totals.lines + ':' + totals.cents;
    // The DRAWER instance — 40a's metrics. The cart page's 26c instance is next, on the same reader.
    var root = document.querySelector('#mini-cart-form');
    if (!root) return;
    var cur = root.querySelector('.b2bsel');
    if (cur && cur.getAttribute('data-b2bsel-sig') === sig) return;
    if (!cur && !totals.lines) return; // nothing there, nothing to show — no work, no loop
    renderSelectionReminder(root, copy, totals);
  }

  // ── 26c — the SAME reminder, on the cart page, under the title ──────────────────────────────
  // Round 14 drew it on 12a and 12c. It sat unbuilt long after its blocker cleared: it reports the
  // order-pad SELECTION, which used to be page-lifetime only, so /cart had nothing to report — but
  // CD-11 was resolved and the persistence built on 2026-08-01 (`waj-b2b-selection`, localStorage,
  // no expiry, see loadSelection/saveSelection). The stale « BLOCKED » notes in main-cart.liquid
  // outlived the blocker by weeks and are deleted with this build.
  //
  // 🔴 ONE READER, TWO DRAWINGS. `selectionTotals()` / `selectionCopy()` are reused verbatim — a
  // second reader over the same basket is exactly how two surfaces start disagreeing about the same
  // number. What differs is METRICS (26c: r12, pad 10/14, max 560, title 11,5/700, sub 10,5 at .6
  // against 40a's 12 / 9-12 / 11 / 10), and those live on `.b2bsel--page` in b2b-cart.css.
  //
  // 🔴 THE COPY IS READ OFF THE HOOK, NOT OFF `#mini-cart` — fixed 2026-08-04, and this is the
  // whole reason 26c never appeared. As built it read the strings off the drawer, on the stated
  // premise that « the drawer is rendered on every page by the layout — including this one ». It is
  // not: `layout/theme.liquid` guards `{% section 'mini-cart' %}` with
  // `{% unless settings.cart_type == 'page' or request.page_type == 'cart' %}`. So on /cart
  // `selectionCopy()` returned four empty strings, the guard below fired, and the block rendered
  // nothing — at EVERY width, not only on desktop.
  //
  // VERIFIED on the dev theme (waj-b2b-theme/dev #192506659182) 2026-08-03: on /cart, `#mini-cart`
  // absent and `.b2bsel` count 0 at both 1440 and 390; on a collection page the DRAWER instance
  // renders correctly at both 1440 (x954, y80) and 390 (x14, y68). Nothing was ever scoped to
  // mobile — the drawer instance simply works everywhere and the page instance nowhere.
  //
  // The strings now come from THEME settings, stamped on the hook in main-cart.liquid: still one
  // set of words for both surfaces, but read from a source that exists on this page.
  function ensurePageSelectionReminder() {
    var hook = document.querySelector('[data-b2bc-sel-hook]');
    if (!hook) return; // not the b2b cart page
    var copy = selectionCopy(hook);
    if (!copy.titleOther && !copy.titleOne) return;
    var totals = selectionTotals();
    var sig = totals.lines + ':' + totals.cents;
    var cur = hook.querySelector('.b2bsel');
    if (cur && cur.getAttribute('data-b2bsel-sig') === sig) return;
    if (!cur && !totals.lines) return;
    renderSelectionReminder(hook, copy, totals, {
      modifier: 'b2bsel--page',
      href: hook.getAttribute('data-sel-href') || ''
    });
  }

  // « Même bandeau en page S12, sous le titre. » The cart page is a full navigation, so the report
  // has to survive it — which is what sessionStorage is for.
  //
  // The reminder rides the SAME observer, and for the same reason: `CartDrawer._rerenderCart`
  // assigns `this.innerHTML`, so anything injected into the form is wiped on every cart mutation
  // and has to be re-asserted rather than injected once. The guard inside each `ensure*` is what
  // stops the observer re-triggering on its own insertion.
  document.addEventListener('DOMContentLoaded', function () {
    ensurePartialBanner();
    ensureSelectionReminder();
    ensurePageSelectionReminder();
    // The basket's own broadcast (see `saveSelection`). The observer below catches CART mutations;
    // this catches SELECTION ones, which never touch the drawer's DOM and were therefore invisible
    // to it. Both readers are signature-guarded, so a redundant call costs nothing.
    document.addEventListener('waj:selection-changed', function () {
      ensureSelectionReminder();
      ensurePageSelectionReminder();
    });

    var host = document.querySelector('#mini-cart') || document.body;
    new MutationObserver(function () {
      ensurePartialBanner();
      ensureSelectionReminder();
      // 26c rides the same observer: the cart page's own list is re-rendered by Focal on every
      // quantity change, and the hook sits outside it — but a cart mutation can empty the selection
      // (the buyer committing it), so the reminder must be re-evaluated, not injected once.
      ensurePageSelectionReminder();
    }).observe(host, { childList: true, subtree: true });
  });

  // ── 32c — the quantity pill's detail tooltip ────────────────────────────────────────────────
  // One open tooltip at a time, page-wide, with the dismiss listeners registered ONCE rather than
  // per pad: Focal rebuilds every <order-pad> on each filter / sort / page change, so listeners
  // bound per-pad would accumulate. Visibility is driven purely by the `hidden` attribute — there
  // is deliberately no CSS :hover rule, because on iOS WebKit keeps :hover on the last element
  // TAPPED until another is tapped, which would leave a tooltip stuck open after a tap.
  var openPill = null;

  function closePill() {
    if (!openPill) return;
    openPill.pill.setAttribute('aria-expanded', 'false');
    if (openPill.tip) openPill.tip.hidden = true;
    openPill = null;
  }

  function showPill(pill, tip) {
    if (openPill && openPill.pill === pill) return;
    closePill();
    if (!tip) return;
    tip.hidden = false;
    pill.setAttribute('aria-expanded', 'true');
    openPill = { pill: pill, tip: tip };
  }

  document.addEventListener('click', function (event) {
    if (!openPill) return;
    if (event.target.closest && event.target.closest('[data-op-pill]') === openPill.pill) return;
    closePill();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') closePill();
  });
  window.addEventListener('scroll', closePill, { passive: true });

  // ── 43b — THE CAP, AND ITS STATE, LIVE ON THE STEPPER ────────────────────────────────────────
  // « le tag ne vient pas de la carte — il vient du STEPPER » (44b). So the arithmetic is wired
  // ONCE here, at the level every consumer of the stepper shares — the Commande rapide card, the
  // PDP pad (S1/S11), the famille pads (S19/S27) and the selection panel — rather than per surface.
  // A rule written on a surface has to be re-written on the next one and eventually is not.
  //
  // The cap is `stock − quantity already in the buyer's cart`. Liquid resolves it at render, which
  // is what makes the FIRST paint honest; this recomputes it on every cart mutation, which is what
  // keeps it honest afterwards — the page renders once and the cart moves under it (another tab,
  // the drawer, a null commit). Every recomputation starts again from `data-op-stock`, the TRUE
  // inventory, so two syncs in a row can never subtract twice.
  var cartQtyMap = null;
  var capsPending = false;

  // One reader for `data-op-stock`, so « absent attribute means uncapped » is decided in one place
  // rather than re-derived (differently) at each call site.
  function stockOf(row) {
    var raw = row && row.getAttribute ? row.getAttribute('data-op-stock') : null;
    if (raw === null || raw === '') return null;
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  function eachPad(fn) {
    document.querySelectorAll('order-pad').forEach(function (pad) {
      if (typeof pad.applyCartQuantities === 'function') fn(pad);
    });
  }

  function pushCartQuantities(cart) {
    if (!cart || !cart.items) return;
    var map = {};
    cart.items.forEach(function (i) { map[i.variant_id] = (map[i.variant_id] || 0) + i.quantity; });
    cartQtyMap = map;
    eachPad(function (pad) { pad.applyCartQuantities(map); });
    // The panel draws from the basket, not from the pads, so it needs the map in its own hands.
    document.querySelectorAll('order-pad-collection').forEach(function (el) {
      if (typeof el.applyCartQuantities === 'function') el.applyCartQuantities(map);
    });
  }

  // Coalesced: Focal fires `cart:updated` and `cart:refresh` back to back for one mutation, and
  // `cart:refresh` frequently arrives with no payload at all. One fetch per frame, never two.
  function refreshCaps(cart) {
    if (cart && cart.items) { pushCartQuantities(cart); return; }
    if (capsPending) return;
    capsPending = true;
    var root = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { capsPending = false; pushCartQuantities(c); })
      // Keep the last known caps rather than falling back to raw stock, which would re-open the
      // exact defect: a cap that promises an addition the commit cannot honour.
      .catch(function () { capsPending = false; });
  }

  document.addEventListener('cart:updated', function (e) { refreshCaps(e && e.detail && e.detail.cart); });
  document.addEventListener('cart:refresh', function (e) { refreshCaps(e && e.detail && e.detail.cart); });

  // The chip is a DOOR, not a wall: nothing on this page can unblock the row — only the cart can —
  // so tapping it opens the cart drawer, and scrolls to the product's group once the drawer has
  // rendered. Delegated at the document so it survives every Focal re-render of #facet-main.
  //
  // `openable-element`'s own `open` property is the mechanism (the same one the selection panel
  // uses); the header's toggle link is the fallback for the window before the element upgrades.
  document.addEventListener('click', function (event) {
    var chip = event.target.closest && event.target.closest('[data-op-allin-chip]');
    if (!chip) return;
    // In Grille mode an overlay link covers the whole card. The chip is not rendered there, but
    // stop the event anyway so a future restructure cannot turn "show me my cart" into a navigation.
    event.preventDefault();
    event.stopPropagation();

    var drawer = document.getElementById('mini-cart');
    if (drawer && 'open' in drawer && drawer.constructor !== HTMLElement) {
      drawer.open = true;
    } else {
      var toggle = document.querySelector('[aria-controls="mini-cart"]');
      if (toggle) { toggle.click(); return; }
      window.location.href = chip.getAttribute('data-op-cart-url') || '/cart';
      return;
    }

    // Land on the line the chip is about, not at the top of a long wholesale cart.
    //
    // 🔴 ROOT CAUSE OF « ça ouvre le panier mais ça ne scrolle pas » (2026-08-03): the lookup was
    // `.b2bm__group a[href="<product.url>"]`, and it could NEVER match. The drawer's group anchors
    // are `line_item.url`, which Shopify returns as `/products/<handle>?variant=<id>`; the chip
    // stamps the bare `product.url`. An exact-href match against two different strings finds
    // nothing, silently, for every product — so the code existed and did nothing. Fixed at the
    // source: `sections/mini-cart.liquid` now stamps `data-b2bm-product-url` on the GROUP.
    //
    // The 260ms timeout was the second half of the problem and is gone too. Focal's
    // `CartDrawer._rerenderCart` fetches the section and assigns `this.innerHTML`, so on a cold
    // open the group we want may not exist yet at 260ms — or may exist and then be REPLACED by a
    // node the callback no longer holds. A fixed delay can only ever be lucky. We wait for the
    // actual render instead: resolve now if the group is already there, otherwise observe the
    // drawer until it appears, with a ceiling so a cart that genuinely lacks the product (a stale
    // chip) does not leave an observer alive.
    var url = chip.getAttribute('data-op-product-url');
    if (!url) return;
    scrollDrawerToProduct(url);
  });

  // Exported from the click handler so the matching rule lives in ONE place.
  // Prefix match, not equality: the stamp is `product.url`, but a locale-prefixed or
  // query-decorated variant of the same URL must still resolve, and a bare `startsWith` on the
  // full URL would let `/products/culotte` match `/products/culotte-longue`. Comparing the stamp
  // to the needle in BOTH directions, anchored on the whole path, is exact for the real cases and
  // refuses the near-miss.
  function drawerGroupFor(url) {
    var groups = document.querySelectorAll('.b2bm__group[data-b2bm-product-url]');
    for (var i = 0; i < groups.length; i++) {
      var stamped = groups[i].getAttribute('data-b2bm-product-url') || '';
      var bare = stamped.split('?')[0];
      var needle = String(url).split('?')[0];
      if (bare === needle) return groups[i];
    }
    return null;
  }

  // ══ 45f — THE ARRIVAL HIGHLIGHT (completes 44b) ═══════════════════════════════════════════════
  // The scroll already worked; this is the only thing 45f adds. #FEFBC4 solid, full row, hold
  // 600ms then fade 900ms to nothing — 1 500ms, ONE run, ease-out.
  //
  // 🔴 YELLOW, NOT MINT, and CD is explicit about why: mint says « fait » (the 43b tag, the
  // conversion). Highlighting an arrival in mint would narrate an addition that did not happen —
  // nothing was added, we are SHOWING a line that already existed. Yellow already means « c'est
  // elle » everywhere else on this store. « Il pointe sans annoncer. »
  //
  // The target is the drawer GROUP, which is what the chip can resolve and what the scroll lands
  // on: the chip stamps `product.url` only, so there is no variant to point at a single size row.
  var ARRIVAL_MS = 1500;

  // The drawer's scroller is Focal's `.drawer__content`, but it is found by walking rather than
  // named: the drawer's inner markup is re-assigned wholesale by `CartDrawer._rerenderCart`, and a
  // hardcoded selector is one refactor away from silently returning null and taking the fallback
  // branch forever.
  function scrollParentOf(el) {
    var node = el && el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var oy = window.getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
      node = node.parentElement;
    }
    return null;
  }

  // FULLY visible, not merely intersecting: `scrollIntoView({block:'nearest'})` still scrolls a
  // partly-clipped element, so a partial test would take the no-scroll branch and highlight before
  // the movement it is supposed to follow.
  function fullyVisibleIn(group, scroller) {
    var r = group.getBoundingClientRect();
    var b = scroller
      ? scroller.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight || document.documentElement.clientHeight };
    return r.top >= b.top - 1 && r.bottom <= b.bottom + 1;
  }

  function highlightArrival(group) {
    if (!group) return;
    // 🔴 RE-TAPPING THE CHIP RE-HIGHLIGHTS — « c'est un pointeur, il se re-pointe ». Removing and
    // re-adding the class in the same task coalesces into NO style change, so the second tap would
    // do nothing; the forced reflow between the two is what makes it a genuinely new run.
    group.classList.remove('is-arriving');
    void group.offsetWidth;
    group.classList.add('is-arriving');
    window.clearTimeout(group._arrivalTimer);
    group._arrivalTimer = window.setTimeout(function () {
      group.classList.remove('is-arriving');
    }, ARRIVAL_MS + 60);
  }

  function armArrivalHighlight(group, scroller, wasVisible) {
    // 🔴 ALREADY VISIBLE → HIGHLIGHT ANYWAY, 100ms after the drawer opens. « Le signal est "voici
    // ta ligne", pas "on a défilé". » Nothing scrolls in this state, so waiting on a scroll event
    // here would wait forever.
    if (wasVisible) {
      window.setTimeout(function () { highlightArrival(group); }, 100);
      return;
    }

    // CD says « départ sur `scrollend`, repli 300ms ». `scrollend` is NOT universally supported
    // (no Safari at time of writing), and a fixed 300ms repli is a guess that lands mid-glide on a
    // long cart. Feature-detect, and fall back on a SETTLE-BY-RE-MEASURE loop — the scroll is over
    // when scrollTop stops changing, which is the thing `scrollend` itself reports.
    var fired = false;
    var ceiling = null;
    function fire() {
      if (fired) return;
      fired = true;
      if (scroller) scroller.removeEventListener('scrollend', fire);
      window.clearTimeout(ceiling);
      highlightArrival(group);
    }

    if (scroller && 'onscrollend' in window) {
      scroller.addEventListener('scrollend', fire);
      // A smooth scroll that has nowhere to go emits no scrollend at all, so the ceiling is not
      // belt-and-braces — it is the only exit for the no-movement case.
      ceiling = window.setTimeout(fire, 1200);
      return;
    }

    if (!scroller) { window.setTimeout(fire, 300); return; }

    var last = scroller.scrollTop;
    var still = 0;
    var deadline = Date.now() + 1200;
    (function tick() {
      if (fired) return;
      var now = scroller.scrollTop;
      if (now === last) { still++; } else { still = 0; last = now; }
      // Three consecutive identical frames ≈ 50ms of stillness: enough that a smooth scroll's
      // slowest final frames are not mistaken for the end, short enough to feel immediate.
      if (still >= 3 || Date.now() > deadline) { fire(); return; }
      window.requestAnimationFrame(tick);
    })();
  }

  function scrollDrawerToProduct(url) {
    var deadline = Date.now() + 3000;
    var observer = null;

    function land(group) {
      if (observer) { observer.disconnect(); observer = null; }
      // 🔴 One frame AFTER the node exists. `scrollIntoView` on a node whose scroll container has
      // not laid out yet is a no-op that reports nothing — the third failure mode behind the
      // original timeout. rAF guarantees layout has run on the freshly-assigned innerHTML.
      window.requestAnimationFrame(function () {
        // Measured BEFORE the scroll is asked for: afterwards the answer is always « visible ».
        var scroller = scrollParentOf(group);
        var wasVisible = fullyVisibleIn(group, scroller);
        if (group.scrollIntoView) group.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        armArrivalHighlight(group, scroller, wasVisible);
      });
    }

    var immediate = drawerGroupFor(url);
    if (immediate) { land(immediate); return; }

    var host = document.getElementById('mini-cart');
    if (!host || typeof MutationObserver === 'undefined') return;

    observer = new MutationObserver(function () {
      var group = drawerGroupFor(url);
      if (group) { land(group); return; }
      // Ceiling: a chip can outlive the cart line it points at (removed in another tab), and an
      // observer with no stop condition would then live for the rest of the session.
      if (Date.now() > deadline) { observer.disconnect(); observer = null; }
    });
    observer.observe(host, { childList: true, subtree: true });
    // Belt-and-braces stop for the case where the drawer never mutates again at all — with no
    // mutation the callback never runs, so the deadline check inside it never fires.
    window.setTimeout(function () {
      if (observer) { observer.disconnect(); observer = null; }
    }, 3000);
  }

  class OrderPad extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.addUrl = this.getAttribute('data-add-url') || '/cart/add';
      this.cartUrl = this.getAttribute('data-cart-url') || '/cart';
      this.isAggregate = this.hasAttribute('data-aggregate');
      this.productTitle = this.getAttribute('data-product-title') || '';
      this.productUrl = this.getAttribute('data-product-url') || '';
      this.productImage = this.getAttribute('data-product-image') || '';
      this.rows = Array.from(this.querySelectorAll('[data-op-row]'));
      this.submitButton = this.querySelector('[data-op-submit]');
      // may be multiple (collection card: inline sum + fold sum)
      this.totalUnitsEls = Array.from(this.querySelectorAll('[data-op-total-units]'));
      this.totalAmountEls = Array.from(this.querySelectorAll('[data-op-total-amount]'));
      this.feedbackEl = this.querySelector('[data-op-feedback]');
      this.totalUnits = 0;
      this.totalCents = 0;

      // 32c — the quantity pill (collection Grille cards only; absent on the PDP pad).
      this.pillEl = this.querySelector('[data-op-pill]');
      this.pillTipEl = this.querySelector('[data-op-pill-tip]');
      this.articleShort = this.getAttribute('data-article-short') || 'art.';
      if (this.pillEl) {
        this.addEventListener('click', this.onPillClick.bind(this));
        // Desktop only. Behind the same pointer test every hover affordance in this build sits
        // behind — a touch device must never acquire a hover state it cannot clear.
        if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.pillEl.addEventListener('mouseenter', this.onPillHover.bind(this, true));
          this.pillEl.addEventListener('mouseleave', this.onPillHover.bind(this, false));
        }
        this.pillEl.addEventListener('focus', this.onPillHover.bind(this, true));
        this.pillEl.addEventListener('blur', this.onPillHover.bind(this, false));
      }

      // Focal's <quantity-selector> already wires the +/- buttons (dispatch change).
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onInput.bind(this));
      if (this.submitButton) this.submitButton.addEventListener('click', this.onSubmit.bind(this));

      // 43b — a pad that upgrades AFTER the last cart sync (every Focal re-render of #facet-main
      // rebuilds all of them) would otherwise render Liquid's caps, which are as old as the page.
      // Liquid painted the first frame correctly; this catches every frame after it.
      if (cartQtyMap) this.applyCartQuantities(cartQtyMap);

      this.recalculate();
    }

    // 43b — recompute every row's cap from TRUE inventory minus what the cart now holds.
    // `data-op-stock` is absent on untracked / oversell-allowed variants, which have no ceiling and
    // are deliberately left alone: no `max`, no state, exactly as before.
    /* 🔴 46f CASE B — « les plafonds sont rafraîchis, les lignes tombées à zéro se convertissent
       en tag ». The stock changed on the SERVER, so `data-op-stock` is stale and there is nothing
       client-side that can read the new number.

       But we do not need the number. A line that could not move AT ALL has, by definition, zero
       remaining capacity right now — whatever the new stock is, it is already fully taken. So set
       its stock to what the cart already holds: cap becomes 0, `applyCartQuantities` converts the
       row to the 43b tag, and the statement is exact rather than inferred.

       (A PARTIAL drop — stock fell but not to the buyer's cart quantity — is not derivable this way
       and is not claimed to be. Shopify clamps that case rather than rejecting it, so it arrives as
       case A and never reaches here.) */
    markCeilingReached(ids) {
      var self = this;
      (ids || []).forEach(function (id) {
        var row = self.rowFor(parseInt(id, 10));
        if (!row) return;
        var inCart = parseInt(row.getAttribute('data-op-incart'), 10) || 0;

        // 🔴 THE TWO SUB-CASES ARE DIFFERENT STATES, AND CONFLATING THEM SHIPPED A LIE.
        // Reported: a size that had run out showed « ✓ Tout au panier » with NOTHING of it in the
        // cart — and the chip is a DOOR, so tapping it opened the cart on a product group that did
        // not contain the line at all.
        //
        //   inCart > 0 → every unit that exists is already hers. The 43b chip is exactly right:
        //                the stock is not gone, it has moved, and the cart is where to act on it.
        //   inCart = 0 → she has none of it and can get none. Nothing has moved anywhere; the size
        //                is simply gone. That is RUPTURE — the same thing she would have seen had
        //                she landed on the page after it sold out, which is the correct test for
        //                what this row should say.
        if (inCart > 0) {
          row.setAttribute('data-op-stock', inCart);
        } else {
          self.markSoldOut(row);
        }
      });
      this.applyCartQuantities(this._lastCartMap || {});
    }

    /* Convert a live row to RUPTURE at runtime. The Liquid renders this state from
       `variant.available` at first paint and there is no sold-out tag in the DOM for an available
       row, so the tag is built here — with the SAME class the Liquid uses, never a variant of it,
       or the two paints of one state would drift.

       `data-op-gone` is what the CSS keys on to retire the stepper and the chip together. It is a
       separate flag from `data-op-allin` on purpose: they are the two halves of the fork above and
       must never both be able to apply. */
    markSoldOut(row) {
      if (row.hasAttribute('data-op-gone')) return;
      row.setAttribute('data-op-gone', '');
      row.removeAttribute('data-op-allin');
      row.setAttribute('data-available', 'false');
      row.classList.add(this.isAggregate ? 'oc-card__row--out' : 'order-pad__row--soldout');

      var input = row.querySelector('[data-op-qty]');
      if (input) input.value = 0;

      // 🔴 ONE METHOD, TWO SHAPES. The PDP pad puts the tag in `.order-pad__cell--qty` and calls it
      // `.order-pad__soldout-tag`; the quick-order CARD puts it straight in the row and calls it
      // `.oc-card__out-tag`. Both are `<order-pad>`, so the aggregate flag is what tells them apart.
      // Each branch uses the class ITS OWN Liquid uses — never a shared new one — so the runtime
      // paint and the server paint of one state stay literally identical.
      var card = this.isAggregate;
      var cell = card ? row : row.querySelector('.order-pad__cell--qty');
      var tagClass = card ? 'oc-card__out-tag' : 'order-pad__soldout-tag text--subdued text--small';
      var probe = card ? '.oc-card__out-tag' : '.order-pad__soldout-tag';
      if (cell && !cell.querySelector(probe)) {
        var tag = document.createElement('span');
        tag.className = tagClass;
        tag.textContent = (this.getAttribute('data-msg-soldout') || '').trim() || 'Rupture';
        cell.appendChild(tag);
      }
      var lineEl = row.querySelector('[data-op-line-total]');
      if (lineEl) lineEl.textContent = '—';
      row.removeAttribute('data-op-active');
    }

    applyCartQuantities(map) {
      this._lastCartMap = map;
      this.rows.forEach(function (row) {
        var stockAttr = row.getAttribute('data-op-stock');
        if (stockAttr === null || stockAttr === '') return;
        var stock = parseInt(stockAttr, 10);
        if (isNaN(stock)) return;

        var inCart = map[parseInt(row.getAttribute('data-variant-id'), 10)] || 0;
        var cap = stock - inCart;
        if (cap < 0) cap = 0;

        row.setAttribute('data-op-incart', inCart);
        var input = row.querySelector('[data-op-qty]');
        if (input) {
          // The merchant's quantity rule is an independent ceiling (PDP pad). An inventory cap may
          // LOWER the effective max, never raise it — so the two are min()'d rather than replaced.
          // The all-in STATE still keys off the inventory cap alone: a quantity rule is not a
          // statement about what is in her cart.
          var ruleMax = parseInt(input.getAttribute('data-op-rulemax'), 10);
          var effective = (!isNaN(ruleMax) && ruleMax < cap) ? ruleMax : cap;
          input.setAttribute('max', String(effective));
          // A quantity typed against the OLD cap is now a promise the commit cannot honour, so a
          // PARTIAL clamp is written into the control. This is the whole defect, in one line.
          //
          // At cap 0 the value is deliberately LEFT ALONE: the stepper is gone behind the chip, so
          // there is nothing on screen to contradict, and preserving it is what lets the stepper
          // return carrying her number when the cart frees the size again. recalculate() excludes
          // the row from every total, so the preserved value is inert until it is takeable.
          if (effective > 0 && (parseInt(input.value, 10) || 0) > effective) input.value = effective;
        }
        row.toggleAttribute('data-op-allin', cap === 0);

        // Re-resolve the chip's « Les {n} pièces… » against the number that just changed.
        var chip = row.querySelector('[data-op-allin-chip]');
        var tpl = this.getAttribute('data-allin-title');
        if (chip && tpl) {
          var title = tpl.split('{n}').join(inCart);
          chip.setAttribute('title', title);
          chip.setAttribute('aria-label', title);
        }
      }, this);
      // The BASKET is clamped separately, by OrderPadCollection.applyCartQuantities — it has to be,
      // because it holds lines whose pads are not rendered. This method owns the markup only.
      this.recalculate();
    }

    onInput(event) {
      if (!event.target.closest('[data-op-qty]')) return;
      var input = event.target;
      var val = parseInt(input.value, 10);
      if (isNaN(val) || val < 0) val = 0;
      var max = input.getAttribute('max');
      if (max != null && val > parseInt(max, 10)) val = parseInt(max, 10);
      input.value = val;
      this.recalculate();
    }

    onPillClick(event) {
      var pill = event.target.closest('[data-op-pill]');
      if (!pill || !this.contains(pill) || pill.disabled) return;
      // In Grille mode the whole card is covered by an overlay link (.oc-card__grille-link). The
      // pill sits above it, so the click lands here — but stop it anyway so a future restructure
      // cannot turn "show me the sizes" into a navigation.
      event.preventDefault();
      event.stopPropagation();
      if (openPill && openPill.pill === pill) closePill();
      else showPill(pill, this.pillTipEl);
    }

    onPillHover(open) {
      if (!this.pillEl || this.pillEl.disabled) return;
      if (open) showPill(this.pillEl, this.pillTipEl);
      else closePill();
    }

    recalculate() {
      var totalUnits = 0;
      var totalCents = 0;
      // 32c — sizes carrying a quantity, in row order, for the pill.
      var picked = [];

      this.rows.forEach(function (row) {
        var input = row.querySelector('[data-op-qty]');
        var qty = input ? (parseInt(input.value, 10) || 0) : 0;
        // 43b — an all-in row contributes NOTHING. Its input keeps whatever was typed (hidden
        // behind the chip) on purpose: emptying the cart makes the size takeable again and the
        // stepper must return carrying her number, not zero. That is the reversibility the chip
        // promises by being a door, so the value is preserved and merely not counted.
        if (row.hasAttribute('data-op-allin')) qty = 0;
        var price = parseInt(row.getAttribute('data-price'), 10) || 0;
        var lineCents = qty * price;
        totalUnits += qty;
        totalCents += lineCents;

        // 🔴 CD S1-1a: `sub: qty > 0 ? fmt(qty * price) : "—"`. A zero line has no amount to state,
        // so it states none. This covers all three ways a row reaches 0: never touched, emptied by
        // the buyer, and sold out (no stepper, so qty can only ever be 0). An all-in row is forced
        // to 0 just above and lands here too — its subtotal is a dash, not « 0,00 € », because the
        // chip beside it already says where those pieces went.
        var lineEl = row.querySelector('[data-op-line-total]');
        if (lineEl) lineEl.textContent = qty > 0 ? formatMoney(lineCents, this.moneyFormat) : '—';
        if (qty > 0) {
          row.setAttribute('data-op-active', '');
          picked.push({ label: row.getAttribute('data-variant-title') || '', qty: qty });
        } else {
          row.removeAttribute('data-op-active');
        }

        // Grey out + at the cap. `max` is emitted only for tracked, deny-policy variants, so an
        // absent max means uncapped. The native stepUp() already refuses to pass max — this makes
        // the ceiling visible instead of leaving the button looking live but inert.
        //
        // 43b — an all-in row is EXCLUDED from the at-max treatment. At cap 0 the stepper is gone
        // (replaced by the chip), so a hint annotating it would annotate a control that is not
        // there. The chip says it.
        //
        // 🔴 46e — `aria-disabled`, NOT `disabled`. A disabled button dispatches no pointer events,
        // so the « Plus de stock » bubble — the thing 46e actually adds — would be unreachable by
        // hover AND by tap in the one state that has something to say. The refusal itself does not
        // depend on the flag: the input carries `max` and <quantity-selector> calls input.stepUp(),
        // which will not pass it. `disabled` is explicitly cleared rather than merely left unset,
        // because a row that was at its ceiling before a cart sync still carries the old property.
        var stepUp = row.querySelector('[data-op-step="up"]');
        var allIn = row.hasAttribute('data-op-allin');
        if (stepUp && input) {
          var maxAttr = input.getAttribute('max');
          var atMax = !allIn && maxAttr !== null && maxAttr !== '' && qty >= parseInt(maxAttr, 10);
          stepUp.disabled = false;
          setCeilingState(stepUp, atMax || allIn);
          row.toggleAttribute('data-op-at-max', atMax);
        }
      }, this);

      this.totalUnits = totalUnits;
      this.totalCents = totalCents;
      this.totalUnitsEls.forEach(function (el) { el.textContent = totalUnits; });
      this.totalAmountEls.forEach(function (el) { el.textContent = formatMoney(totalCents, this.moneyFormat); }, this);
      if (this.submitButton) this.submitButton.disabled = totalUnits === 0;
      this.classList.toggle('order-pad--has-qty', totalUnits > 0);
      if (totalUnits > 0) this.showFeedback('', null);
      this.updatePill(picked, totalUnits);

      if (this.isAggregate) this.dispatchEvent(new CustomEvent('order-pad:change', { bubbles: true }));
    }

    // 32a/32b/32c — the pill is derived from this card's OWN rows, which restore() keeps in step
    // with the collection basket after every re-render. It recalculates on each quantity edit and
    // survives a mode switch, which is the whole point: changing view never hides the work.
    updatePill(picked, totalUnits) {
      var pill = this.pillEl;
      if (!pill) return;
      var tip = this.pillTipEl;

      if (openPill && openPill.pill === pill) closePill();
      if (tip) tip.hidden = true;

      if (!picked.length) {
        pill.hidden = true;
        pill.disabled = true;
        pill.classList.remove('is-aggregate');
        pill.removeAttribute('aria-label');
        return;
      }

      pill.hidden = false;

      if (picked.length <= 2) {
        // 1–2 sizes are listed in full — decoration, nothing to unfold, so not focusable.
        pill.textContent = picked.map(function (p) { return p.label + ' × ' + p.qty; }).join(' · ');
        pill.disabled = true;
        pill.classList.remove('is-aggregate');
        pill.removeAttribute('aria-label');
        return;
      }

      // 3+ sizes: three stacked pills would eat the image, and worse at 390. The aggregate says
      // the essential thing — work lives here — and the ink border is the affordance for detail.
      pill.textContent = totalUnits + ' ' + this.articleShort;
      pill.disabled = false;
      pill.classList.add('is-aggregate');
      pill.setAttribute('aria-label', picked.map(function (p) { return p.label + ' × ' + p.qty; }).join(', '));

      if (tip) {
        // 40d — TWO COLUMNS, size left and « × n » right. CD rejected both its own left-align and
        // our right-align: right-align flushes the last character, not the « × », so « 2XL × 4 »
        // still stepped against « S × 6 ». A grid makes sizes and quantities each their own column.
        // Built as nodes, not innerHTML — variant titles are merchant data.
        while (tip.firstChild) tip.removeChild(tip.firstChild);
        picked.forEach(function (p) {
          var size = document.createElement('span');
          size.textContent = p.label;
          var qty = document.createElement('span');
          qty.className = 'oc-card__pq-qty';
          qty.textContent = '× ' + p.qty;
          tip.appendChild(size);
          tip.appendChild(qty);
        });
      }
    }

    getTotals() { return { units: this.totalUnits || 0, cents: this.totalCents || 0 }; }

    rowFor(variantId) {
      return this.rows.find(function (r) { return parseInt(r.getAttribute('data-variant-id'), 10) === variantId; });
    }

    qtyFor(variantId) {
      var row = this.rowFor(variantId);
      if (!row) return 0;
      var input = row.querySelector('[data-op-qty]');
      return input ? (parseInt(input.value, 10) || 0) : 0;
    }

    setQty(variantId, qty) {
      var row = this.rowFor(variantId);
      if (!row) return;
      var input = row.querySelector('[data-op-qty]');
      if (!input) return;
      if (qty < 0) qty = 0;
      var max = input.getAttribute('max');
      if (max != null && qty > parseInt(max, 10)) qty = parseInt(max, 10);
      input.value = qty;
      this.recalculate();
    }

    getSelected() {
      var self = this;
      var out = [];
      this.rows.forEach(function (row) {
        var input = row.querySelector('[data-op-qty]');
        var qty = input ? (parseInt(input.value, 10) || 0) : 0;
        // Same exclusion as recalculate(): an all-in row is not part of what the commit produces.
        if (row.hasAttribute('data-op-allin')) qty = 0;
        if (qty > 0) {
          var price = parseInt(row.getAttribute('data-price'), 10) || 0;
          out.push({
            variantId: parseInt(row.getAttribute('data-variant-id'), 10),
            sku: row.getAttribute('data-sku') || '',
            variantTitle: row.getAttribute('data-variant-title') || '',
            price: price, qty: qty, lineCents: price * qty,
            productTitle: self.productTitle, productUrl: self.productUrl, productImage: self.productImage
          });
        }
      });
      return out;
    }

    collectItems() {
      return this.getSelected().map(function (s) { return { id: s.variantId, quantity: s.qty }; });
    }

    setLoading(state) {
      if (!this.submitButton) return;
      this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
      this.submitButton.classList.toggle('is-loading', state);
      this.submitButton.disabled = state || this.totalUnits === 0;
    }

    showFeedback(message, kind) {
      if (!this.feedbackEl) return;
      this.feedbackEl.textContent = message;
      this.feedbackEl.hidden = !message;
      this.feedbackEl.classList.remove('order-pad__feedback--error', 'order-pad__feedback--success');
      if (kind) this.feedbackEl.classList.add('order-pad__feedback--' + kind);
    }

    /* One POST. Resolves { ok, data } and never rejects on a 4xx — the caller decides. */
    postItems(items, withSections) {
      var body = { items: items };
      if (withSections) { body.sections = ['mini-cart']; body.sections_url = window.location.pathname; }
      return fetch(this.addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(function (data) { return { ok: response.ok, data: data }; });
      });
    }

    /* 🔴 42c / CD-124 ON THE PDP PAD — ported 2026-08-07, operator report.
       This surface had NEVER carried the partial-add logic: 42c was built on the collection
       controller (`OrderPadCollection`) alone, and `OrderPad.onSubmit` was still the pre-42c code —
       any non-2xx became Shopify's raw description in a pink band PLUS Focal's red
       `cart-notification` bar, with nothing added. Reproduced by the operator: 2XL taken to zero in
       admin, then 5 × 2XL + 5 × 3XL + 5 × 4XL from a stale page → nothing added at all, two errors.

       THE PREMISE BOTH EARLIER VERSIONS SHARED, AND THAT THIS BREAKS: « nothing landed ⇒ genuine
       failure ». `/cart/add.js` behaves two ways on a stock condition — it CLAMPS and partially
       adds when the line merely asks for more than is available, and it REJECTS THE WHOLE PAYLOAD
       when a line cannot move at all. The second case is not a failure; it is exactly what the
       report exists to narrate, and one dead line was destroying an entire wholesale order.

       So: atomic stays the fast path; when it lands nothing we re-send line by line, which is the
       only way the other sizes can still get in. (The per-line loop existed in the first 42c commit
       and was deleted as « waste built on the wrong premise » — the premise correction was right
       about clamping and wrong to conclude the loop was never needed. It is needed here.) */
    onSubmit() {
      var selected = this.getSelected();
      if (!selected.length) return;

      var self = this;
      var items = selected.map(function (s) { return { id: s.variantId, quantity: s.qty }; });
      var labels = {};
      selected.forEach(function (s) {
        labels[s.variantId] = s.variantTitle ? (s.productTitle + ' — ' + s.variantTitle) : s.productTitle;
      });

      this.setLoading(true);
      this.showFeedback('', null);
      savePartialReport(null);

      cartQuantitiesByVariant(this.cartUrl).then(function (before) {
        return self.postItems(items, true).then(function (result) {
          if (result.ok) return self.finishAdd(result.data.sections, [], []);

          return cartQuantitiesByVariant(self.cartUrl).then(function (after) {
            /* Something landed → Shopify clamped. Name every line that came up short. */
            if (after.total > before.total) {
              var clamped = [], clampedIds = [];
              items.forEach(function (it) {
                var expected = (before.map[it.id] || 0) + it.quantity;
                if ((after.map[it.id] || 0) < expected) { clamped.push(labels[it.id]); clampedIds.push(it.id); }
              });
              return self.finishAdd(null, clamped, clampedIds);
            }

            /* 🔴 CASE B — the race (46f §7). Nothing moved, so re-send singly: a line that cannot
               move at all makes Shopify reject the WHOLE payload, and the others must still get in. */
            return addItemsRecover(self.addUrl, self.cartUrl, items, true).then(function (res) {
              if (!res.landed.length) {
                /* Not one line could move. Still not a « network » failure — nothing broke, the
                   world changed — so 46f's rule holds: our own sentence, never Shopify's raw
                   description, and NEVER Focal's red bar. See killFocalErrorBar below. */
                savePartialReport(items.map(function (it) { return labels[it.id]; }).filter(Boolean), 'pad');
                self.showFeedback('', null);
                self.markCeilingReached(items.map(function (it) { return it.id; }));
                self.setLoading(false);
                ensurePartialBanner();
                return;
              }
              /* Some lines got in on the retry: an add DID happen, the drawer opens, so this is
                 case A and the report belongs at the cart. */
              // The PDP pad is UNCHANGED by the 2026-08-29 selection ruling: that ruling was about
              // the sticky selection, and this surface is one product with its own footer CTA. So
              // it keeps treating both verdicts alike — refused AND deferred are reported and both
              // stay in the pad. Spelled out rather than left to `res.refused`, which stopped
              // meaning « everything that did not land » when `deferred` was split out of it.
              var stuck = res.refused.concat(res.deferred);
              return self.finishAdd(null, stuck.map(function (id) { return labels[id]; }), stuck);
            });
          });
        });
      }).catch(function () {
        self.showFeedback((self.getAttribute('data-msg-network') || 'Une erreur réseau est survenue. Réessayez.'), 'error');
        self.setLoading(false);
      });
    }

    /* The one success path — reached by a clean add AND by a partial one, because CD ruled a
       partial add IS a success (42c). No error feedback, and deliberately NO `cart-notification`
       red bar: nothing failed, the world changed. */
    finishAdd(sections, reportLines, keepIds) {
      var self = this;
      var addedUnits = this.totalUnits;
      /* CASE A — something landed, so the drawer is about to open and the report goes with the
         buyer, to the cart. 42c, unchanged. */
      savePartialReport(reportLines, 'cart');
      return refreshCartAndOpen(this.cartUrl, sections)
        .then(function () {
          var opener = self.closest('quick-buy-drawer, quick-buy-popover');
          if (opener) opener.open = false;
          /* Rows that landed are cleared; a refused row keeps the buyer's entry so one tap re-sends
             what can now pass. 46d's « adding from the pad zeroes the pad » is about the lines that
             were actually added — losing the rest would be losing their work. */
          self.reset(keepIds && keepIds.length ? keepIds : null);
          self.setLoading(false);
          if (!(reportLines && reportLines.length)) {
            self.showFeedback((self.getAttribute('data-added-prefix') || '✓ Ajouté au panier —') + ' ' + addedUnits + ' ' + (self.getAttribute('data-article-word') || 'articles'), 'success');
          }
        })
        .catch(function () { window.location.href = self.cartUrl; });
    }

    /* `keepIds` — variant ids whose quantity must SURVIVE the reset. Used by the partial add: the
       lines that landed are cleared, the lines that were refused keep the buyer's entry so one tap
       re-sends what can now pass (46f: « la saisie restante est intacte »). */
    reset(keepIds) {
      this.rows.forEach(function (row) {
        if (keepIds && keepIds.indexOf(parseInt(row.getAttribute('data-variant-id'), 10)) !== -1) return;
        var input = row.querySelector('[data-op-qty]');
        if (input) input.value = 0;
      });
      this.recalculate();
    }
  }

  customElements.define('order-pad', OrderPad);

  class OrderPadCollection extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.addUrl = this.getAttribute('data-add-url') || '/cart/add';
      this.cartUrl = this.getAttribute('data-cart-url') || '/cart';
      this.minCents = parseInt(this.getAttribute('data-min-cents'), 10) || 0;
      // The BASKET, and the source of truth for the sticky bar and add-all: variantId -> {qty, price}.
      // It is NOT the DOM, because Focal destroys and rebuilds the whole product area on every
      // filter / sort / page change — a DOM-derived total silently drops everything the buyer typed
      // on any other page. Wholesale buyers build an order across several pages, so the selection has
      // to outlive the markup that collected it.
      //
      // 🔴 NOW PERSISTED (ruled in an earlier session, built 2026-08-01). Page-lifetime was the
      // wrong lifetime: an in-memory basket died on every full navigation, so a buyer who clicked
      // through to a PDP and back — or simply reloaded — lost an order they had been assembling
      // across pages, which is the exact behaviour the basket exists to prevent. localStorage,
      // NO EXPIRY, cleared on add-to-cart and on an explicit clear.
      // CROSS-DEVICE IS OUT OF REACH and not attempted: it needs the selection on the customer
      // record, which is a backend change, not a theme one.
      this.storageKey = 'waj-b2b-selection';
      this.selection = this.loadSelection();
      this.unitsEl = this.querySelector('[data-oc-units]');
      this.amountEl = this.querySelector('[data-oc-amount]');
      this.submitBtn = this.querySelector('[data-oc-submit]');
      this.feedbackEl = this.querySelector('[data-oc-feedback]');
      // Canvas S10 sticky bar (empty↔total, plural, min-order progress, added)
      this.emptyEl = this.querySelector('[data-oc-empty]');
      this.totalEl = this.querySelector('[data-oc-total]');
      this.unitsPluralEl = this.querySelector('[data-oc-units-plural]');
      this.refsEl = this.querySelector('[data-oc-refs]');
      this.refsPluralEl = this.querySelector('[data-oc-refs-plural]');
      this.addedEl = this.querySelector('[data-oc-added]');
      this.totalUnits = 0;

      // ── The sticky bar, CD 22d / 24 / 26 ────────────────────────────────────────────────────
      // The bar measures the PROJECTED order — cart + selection (15a; 26a made it explicit for the
      // resting state). The cart half is stamped server-side and re-stamped after each add.
      // MONEY — what the buyer pays, discount included. Drives « panier X € HT », the cart-context
      // line and the header pill, and must agree with the drawer and the cart page to the cent.
      this.cartCents = parseInt(this.getAttribute('data-cart-cents'), 10) || 0;
      // THRESHOLD BASIS — the subtotal BEFORE the privilege discount, because that is what the
      // −10 % Function qualifies on. 🔴 Never render this as money: for one commit on 2026-08-12
      // the two were a single attribute, and the bar showed « panier 1000 € » against the header's
      // 900 €. The discounted total is made legible by 48c's discount LINE, not by a bigger number.
      this.cartBasisCents = parseInt(this.getAttribute('data-cart-basis-cents'), 10) || this.cartCents;
      this.htSuffix = this.getAttribute('data-ht-suffix') || 'HT';
      this.tplCartline = this.getAttribute('data-bar-cartline') || '';
      this.tplCartRest = this.getAttribute('data-bar-cart-rest') || '';
      this.labelShow = this.getAttribute('data-bar-show') || 'Afficher';
      this.labelHide = this.getAttribute('data-bar-hide') || 'Masquer';
      this.tplPanelFooter = this.getAttribute('data-panel-footer') || '';
      this.panelLineWord = this.getAttribute('data-panel-line-word') || 'ligne';
      // 41a — the grouped panel's product-header summary.
      this.tplPanelGroup = this.getAttribute('data-panel-group-summary') || '';
      this.panelSizeWord = this.getAttribute('data-panel-size-word') || 'taille';
      this.unitSuffix = this.getAttribute('data-unit-suffix') || '';
      this.articleWord = this.getAttribute('data-article-word') || 'article';

      this.barEl = this.querySelector('[data-oc-bar]');
      this.cartlineEl = this.querySelector('[data-oc-cartline]');
      this.cartRestEl = this.querySelector('[data-oc-cartrest]');
      this.toggleEl = this.querySelector('[data-oc-toggle]');
      this.toggleLabelEl = this.querySelector('[data-oc-toggle-label]');
      this.errEl = this.querySelector('[data-oc-err]');
      this.retryEl = this.querySelector('[data-oc-retry]');
      this.panelEl = this.querySelector('[data-oc-panel]');
      // The bar's ground/padding element. The tap target binds here rather than on the bar, so the
      // panel — a sibling, not a child — is outside the hit area by construction.
      this.surfaceEl = this.querySelector('.ocg__bar-surface');
      this.panelTableEl = this.querySelector('[data-oc-panel-table]');
      this.panelOpen = false;
      // (The six desktop column-header labels that used to be read here are GONE — 42a/CD-127
      // retires the column header itself, so the settings that fed it were removed with it.)
      this.panelTotalLabel = this.getAttribute('data-panel-total-label') || 'Total';
      this.panelRemoveLabel = this.getAttribute('data-panel-remove') || 'Retirer';
      // Must differ from the per-size label: both ✕ said « Retirer cette ligne », so on hover the
      // two were indistinguishable although one removes a size and the other removes the product.
      this.panelRemoveGroupLabel = this.getAttribute('data-panel-remove-group') || this.panelRemoveLabel;
      // 43b/44b — the all-in chip's copy. Read here rather than per row: the panel re-renders on
      // every keystroke and the strings never change.
      this.allinLabel = this.getAttribute('data-allin-label') || '';
      this.allinTitle = this.getAttribute('data-allin-title') || '';
      // 46e — the one ceiling sentence, from the theme locale the cart pair already renders.
      this.ceilingLabel = this.getAttribute('data-ceiling-label') || '';
      // 43c — the null-add voice. One message for the bar, one short form for every money slot that
      // has nothing to state (group subtotals, the panel foot), so the two cannot drift apart.
      this.nullAddEl = this.querySelector('[data-oc-nulladd]');
      this.nullAddMessage = this.getAttribute('data-nulladd-message') || '';
      this.nullAddShort = this.getAttribute('data-nulladd-short') || '';
      // 🔴 The cart quantities the panel's caps are computed against, SEEDED FROM LIQUID.
      // Not left empty until the first cart event: the panel draws from the BASKET, and a basket
      // entry carries `stock` but knows nothing about the cart — so an empty map would make
      // `capFor` return raw stock and the panel would render a live stepper for a size the cards
      // beside it are already showing as all-in. The same delimited string the row caps were
      // resolved from, so there is exactly ONE source for both.
      this.cartQtyMap = this.parseCartQty(this.getAttribute('data-cart-qty'));

      // The live gauge contract (see snippets/b2b-threshold-gauge.liquid). Liquid resolves the
      // thresholds and the copy — it can, because the franco reads the company location's shipping
      // country and the −10 % reads the customer's tags, neither of which JS can see — and hands
      // them over here so the SAME grammar can be re-rendered as the selection moves, rather than a
      // JS twin drifting away from the cart page's gauge (audit B6).
      this.gaugeEl = this.querySelector('[data-b2bg-live]');
      if (this.gaugeEl) {
        var g = this.gaugeEl;
        this.gauge = {
          min: parseInt(g.getAttribute('data-b2bg-min'), 10) || 0,
          franco: parseInt(g.getAttribute('data-b2bg-franco'), 10) || 0,
          francoLabel: g.getAttribute('data-b2bg-franco-label') || '',
          discount: parseInt(g.getAttribute('data-b2bg-discount'), 10) || 0,
          discountLabel: g.getAttribute('data-b2bg-discount-label') || '',
          msgEmpty: g.getAttribute('data-b2bg-msg-empty') || '',
          msgBelow: g.getAttribute('data-b2bg-msg-below') || '',
          msgFranco: g.getAttribute('data-b2bg-msg-franco') || '',
          msgDiscount: g.getAttribute('data-b2bg-msg-discount') || '',
          msgDiscountNoFranco: g.getAttribute('data-b2bg-msg-discount-nofranco') || '',
          msgAllPending: g.getAttribute('data-b2bg-msg-all-pending') || '',
          msgAllPendingFranco: g.getAttribute('data-b2bg-msg-all-pending-franco') || '',
          minStateOn: g.getAttribute('data-b2bg-minstate-on') || '',
          minStatePending: g.getAttribute('data-b2bg-minstate-pending') || '',
          chipAll: g.getAttribute('data-b2bg-chip-all') || '',
          chipBoth: g.getAttribute('data-b2bg-chip-both') || '',
          chipDiscount: g.getAttribute('data-b2bg-chip-discount') || '',
          chipMin: g.getAttribute('data-b2bg-chip-min') || '',
          chipMinPending: g.getAttribute('data-b2bg-chip-min-pending') || '',
          tipFormat: g.getAttribute('data-b2bg-tip-format') || '',
          tipMin: g.getAttribute('data-b2bg-tip-min') || '',
          tipFranco: g.getAttribute('data-b2bg-tip-franco') || '',
          tipDiscount: g.getAttribute('data-b2bg-tip-discount') || ''
        };
      }

      // 🔴 THE GRID IS NO LONGER AN ANCESTOR — 2026-08-06, when the bar went site-wide.
      // This element used to WRAP the collection order grid, so every pad was a descendant and
      // `order-pad:change` / `input` / `change` simply bubbled into it. It now lives in
      // `sections/b2b-order-bar.liquid`, rendered from the layout on every page, and what is left
      // on the collection page is a plain `<div class="ocg" data-oc-grid>` elsewhere in the
      // document. So the pad wiring hangs off THAT element, looked up once here.
      //
      // Two consequences, and the second is the load-bearing one:
      //   1. On a page with no grid (PDP, /search, /, /cart) `gridEl` is null. That is the normal
      //      case now, not an error: the panel and the bar render from the BASKET — localStorage —
      //      so they are fully functional with no pad on screen at all. That is the whole point of
      //      the move: a 195 € selection used to become invisible the moment the buyer left the
      //      collection page.
      //   2. 🔴 IT IS THE GRID, NOT THE DOCUMENT. Listening on `document` would fold the PDP's own
      //      `<order-pad>` into the selection basket — and that pad is not a selection surface: it
      //      commits straight to the cart with its own footer CTA. Scoping to the grid is what
      //      keeps « ajouter au panier » on a PDP meaning the CART and the bar meaning the
      //      SELECTION. Do not widen it to the document.
      this.gridEl = document.querySelector('[data-oc-grid]');
      this.onPadChange = this.recalc.bind(this);
      this.onRemember = this.remember.bind(this);
      this.onFold = this.onFoldClick.bind(this);
      if (this.gridEl) this.gridEl.addEventListener('order-pad:change', this.onPadChange);
      if (this.submitBtn) this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      if (this.toggleEl) this.toggleEl.addEventListener('click', this.togglePanel.bind(this));

      // 🔴 41b/2a — at 390 the WHOLE BAR except the CTA is the tap target: « toute la barre hors CTA
      // est la cible tap — la pill est l'affordance visible, pas la seule zone ». At 390 the pill
      // alone was easy to miss entirely, and it is the only route into the panel.
      //
      // Bound on the bar SURFACE, not the bar, so the panel (a sibling) is outside it by
      // construction — a tap inside the open panel must never toggle it shut. The CTA, the retry
      // button and the toggle itself are excluded explicitly: the toggle has its own handler and
      // would otherwise fire twice, cancelling itself.
      if (this.surfaceEl) {
        this.surfaceEl.addEventListener('click', this.onBarTap.bind(this));
        this.barEl.setAttribute('data-oc-bartap', '');
      }
      if (this.retryEl) this.retryEl.addEventListener('click', this.onRetry.bind(this));
      if (this.panelTableEl) {
        this.panelTableEl.addEventListener('click', this.onPanelClick.bind(this));
        // The panel's stepper is Focal's <input-number> now, so quantities can be typed as well as
        // stepped. `change` rather than `input`: input-number clamps on change, and reacting to
        // every keystroke would re-render the panel out from under the cursor.
        this.panelTableEl.addEventListener('change', this.onPanelInput.bind(this));
      }

      // Open/close is owned by Focal's openable-element now, so we listen to its events rather than
      // to whoever called setPanel. A close via the scrim, the ✕, Esc or the focus trap therefore
      // lands in exactly the same place as a close via the toggle — one state path, no away-tap
      // listener on the document.
      this.onPanelToggled = this.onPanelToggled.bind(this);
      if (this.barEl) {
        this.barEl.addEventListener('openable-element:open', this.onPanelToggled);
        this.barEl.addEventListener('openable-element:close', this.onPanelToggled);
      }

      // 🔴 The bar has to LEARN when the cart changes underneath it. `cartCents` was stamped from
      // Liquid at render and only ever re-stamped by our own add-to-cart — so emptying the cart in
      // the drawer (or on the cart page, or in another tab) left « + panier 103,05 € = total … »
      // on screen against a cart of 0, and only a reload cleared it.
      // Same events `<b2b-cart-total>` already listens to, which is Focal's own cart contract.
      this.onCartChanged = this.onCartChanged.bind(this);
      document.addEventListener('cart:updated', this.onCartChanged);
      document.addEventListener('cart:refresh', this.onCartChanged);

      // 17b and 17c are different compositions, not one layout at two widths, so a resize across
      // the breakpoint has to re-render rather than restyle.
      var self = this;
      this.desktopQuery = window.matchMedia('(min-width: 1000px)');
      this.onBreakpoint = function () { self.recalc(); };
      if (this.desktopQuery.addEventListener) this.desktopQuery.addEventListener('change', this.onBreakpoint);

      // Record into the basket from REAL user interaction only. input/change fire on typing and on
      // Focal's <quantity-selector> +/- buttons, and never when a value is set programmatically —
      // which is exactly what keeps restore() below from recording its own writes back over the
      // basket. These listeners are on the GRID (see the gridEl note above), so they run after each
      // OrderPad's own handler has already clamped input.value to its max.
      if (this.gridEl) {
        this.gridEl.addEventListener('input', this.onRemember);
        this.gridEl.addEventListener('change', this.onRemember);
      }

      // The Saisie directe ↔ Repliées mode pill is DELETED (18d / CD-16) — the sticky sub-header's
      // Grille / Commande rapide toggle replaces it. setMode() and the per-card fold below stay in
      // place but are now unreachable: nothing enters .is-fold.
      this.modeBtns = { inline: null, fold: null };
      // Per-card fold expand/collapse (delegated onto the grid)
      if (this.gridEl) this.gridEl.addEventListener('click', this.onFold);

      // Focal's ProductFacet re-renders by replacing #facet-main.innerHTML (theme.js ~5918), which
      // destroys and recreates every <order-pad> on any filter / sort / AJAX page change. The GRID
      // is an ANCESTOR of #facet-main (the beige panel is full-bleed per CD S10, so it must wrap
      // the facet, not sit inside it) and is not itself replaced. Two consequences:
      //   1. `pads` is a LIVE getter (below) — never a cached array of detached stale nodes.
      //   2. this observer runs restore() + recalc() after a re-render, writing the basket back into
      //      the fresh markup. The bar itself needs no rescue: it is in another section entirely now
      //      and totals the basket, not the pads on screen.
      // childList on #facet-main ONLY (not subtree): recalc() writes textContent into the bar,
      // which is outside #facet-main — a subtree observer would loop.
      var facetMain = this.gridEl ? this.gridEl.querySelector('#facet-main') : null;
      if (facetMain && window.MutationObserver) {
        this.facetObserver = new MutationObserver(this.onFacetRerender.bind(this));
        this.facetObserver.observe(facetMain, { childList: true });
      }

      // 🔴 PUBLISH THE BAR'S HEIGHT so the page can clear it (`body { padding-bottom }` in
      // order-pad-collection.css). The bar is `position: fixed` since it went site-wide, so it
      // hovers over the last band of every document — the footer's legal line, the cart's own CTA.
      // MEASURED, never a constant: the bar is ~114px at rest and ~139px with the mint chip, and it
      // re-lays out whenever the selection or the gauge phase moves. The SURFACE is measured, not
      // the bar: `.ocg__bar` also contains the absolutely-positioned panel, and while open its own
      // box is the whole unit — padding the body by that would jump the page every time the panel
      // opened.
      if (this.surfaceEl) {
        var publishBarHeight = function () {
          var h = Math.round(self.surfaceEl.getBoundingClientRect().height);
          document.documentElement.style.setProperty('--ocg-bar-h', h + 'px');
        };
        publishBarHeight();
        if (window.ResizeObserver) {
          this.barSizeObserver = new ResizeObserver(publishBarHeight);
          this.barSizeObserver.observe(this.surfaceEl);
        } else {
          window.addEventListener('resize', publishBarHeight);
        }
      }

      // A restored basket has to be written back into the markup on FIRST paint, not only after a
      // Focal re-render — otherwise the bar would show a total the cards themselves contradict
      // (empty steppers under a « 27 articles » recap). restore() is the same path the re-render
      // uses, so persistence reuses it rather than adding a second way to seed the DOM.
      this.restore();
      this.recalc();
    }

    disconnectedCallback() {
      if (this.facetObserver) this.facetObserver.disconnect();
      if (this.barSizeObserver) this.barSizeObserver.disconnect();
      // Bound OUTSIDE this element, so they do not die with it. (The away-tap document listener
      // that used to sit here is gone — Focal's scrim owns close-on-away now.) The grid listeners
      // join that list since the grid stopped being an ancestor: they would otherwise survive this
      // element and fire into a detached controller.
      if (this.gridEl) {
        this.gridEl.removeEventListener('order-pad:change', this.onPadChange);
        this.gridEl.removeEventListener('input', this.onRemember);
        this.gridEl.removeEventListener('change', this.onRemember);
        this.gridEl.removeEventListener('click', this.onFold);
      }
      document.removeEventListener('cart:updated', this.onCartChanged);
      document.removeEventListener('cart:refresh', this.onCartChanged);
      if (this.desktopQuery && this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener('change', this.onBreakpoint);
      }
    }

    // Live — see the re-render note in connectedCallback.
    // Filtered to UPGRADED elements: while #facet-main's innerHTML is being replaced, custom
    // elements upgrade in document order, and each upgraded <order-pad> calls recalculate() from
    // its own connectedCallback, which bubbles order-pad:change up here — at which point later
    // siblings are still plain HTMLElements with no getTotals. Without this filter every re-render
    // threw a TypeError per pad and aborted the running recalc mid-iteration.
    get pads() {
      if (!this.gridEl) return [];
      return Array.from(this.gridEl.querySelectorAll('order-pad')).filter(function (pad) {
        return typeof pad.getTotals === 'function';
      });
    }

    // ── Basket persistence across Focal's re-renders ────────────────────────────────────────────
    remember(event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var input = target.closest('[data-op-qty]');
      if (!input) return;
      var row = input.closest('[data-op-row]');
      if (!row) return;

      var id = row.getAttribute('data-variant-id');
      if (!id) return;
      var qty = parseInt(input.value, 10) || 0;

      if (qty > 0) {
        var pad = row.closest('order-pad');
        // The full title is "Culotte taille haute coton bio - Dalida": the catalogue is size-only
        // and every colourway is its OWN product (client KB), so the colour lives in the title
        // after " - ". CD's panel row splits them — name on top, "Dalida · S" beneath (24a).
        var fullTitle = pad ? (pad.getAttribute('data-product-title') || '') : '';
        var titleParts = fullTitle.split(' - ');
        this.selection[id] = {
          qty: qty,
          price: parseInt(row.getAttribute('data-price'), 10) || 0,
          // Carried so the canvas summary can count distinct références without the DOM —
          // the pad this line came from may not be rendered when the bar is redrawn.
          productId: pad ? pad.getAttribute('data-product-id') : null,
          // 41a — the grouped panel's product header is CLICKABLE, to that product's order-pad,
          // exactly as in the cart. Captured at entry time for the same reason as everything else
          // here: the originating pad may be long gone from the DOM by the time the panel renders.
          url: pad ? (pad.getAttribute('data-product-url') || '') : '',
          // Same reasoning for the panel: it draws from the basket, so every field it shows has to
          // be captured at entry time. A panel built from the DOM would lose every line typed on a
          // page or filter state Focal has since torn down.
          name: titleParts[0] || fullTitle,
          print: titleParts.length > 1 ? titleParts[titleParts.length - 1] : '',
          // The VARIANT image, matching what the cart shows — the row falls back to the product's
          // featured media itself when the variant carries none, so this is never empty by accident.
          image: row.getAttribute('data-variant-image') || (pad ? (pad.getAttribute('data-product-image') || '') : ''),
          size: row.getAttribute('data-variant-title') || '',
          sku: row.getAttribute('data-sku') || '',
          // The variant's own position in the product's option order — the ONLY authority for
          // « S before M before L ». Captured at entry because the basket outlives the markup.
          pos: parseInt(row.getAttribute('data-variant-position'), 10) || 0,
          // 43b/44b — TRUE inventory, captured at entry for the same reason as everything else in
          // here: the panel recomputes `stock − cart` for a line whose pad may be long gone from
          // the DOM. `null` means untracked/oversell — no ceiling, therefore never an all-in state.
          // Entries persisted before this shipped carry no `stock` and degrade to exactly that.
          stock: stockOf(row)
        };
      } else {
        delete this.selection[id];
      }
      this.recalc();
    }

    // Write the basket back into whatever markup is on screen now. Called after a re-render, so the
    // buyer sees their quantities again when they page or filter back to a product they had typed on.
    restore() {
      var selection = this.selection;
      this.pads.forEach(function (pad) {
        var touched = false;
        pad.rows.forEach(function (row) {
          var entry = selection[row.getAttribute('data-variant-id')];
          var input = row.querySelector('[data-op-qty]');
          if (!input) return;
          var want = entry ? entry.qty : 0;
          if (parseInt(input.value, 10) === want) return;
          input.value = want;
          touched = true;
        });
        if (touched) pad.recalculate();
      });
    }

    onFacetRerender() {
      this.restore();
      this.recalc();
    }

    // ── 43b — the cap applied to the BASKET, not only to what is on screen ──────────────────────
    // The pads clamp their own inputs (OrderPad.applyCartQuantities), but a wholesale basket spans
    // pages and filter states Focal has long since torn down — those lines have no input to clamp
    // and would otherwise sail into the add carrying a quantity the cart can no longer take. The
    // basket is the source of truth everywhere else in this file; it has to be here too.
    applyCartQuantities(map) {
      this.cartQtyMap = map || {};
      var selection = this.selection;
      var changed = false;
      var self = this;
      Object.keys(selection).forEach(function (id) {
        var entry = selection[id];
        var cap = self.capFor(id, entry);
        // A PARTIAL clamp is a real correction and is written down: cap 3 against a typed 6 leaves
        // a 6 that the commit cannot honour, which is the whole defect.
        if (cap === null || cap === 0 || entry.qty <= cap) return;
        entry.qty = cap;
        changed = true;
        self.mirrorToPad(id, cap);
      });
      if (changed) this.recalc();
      else if (this.panelOpen) this.renderPanel(this.totals());
    }

    // 🔴 THE CAP IS DERIVED, NEVER STORED — and at cap 0 the line is NOT deleted.
    // Storing it would make the state one-way: the buyer empties the cart, the size becomes
    // takeable again, and her line would be gone. 44b is explicit that the row survives (« la
    // rangée ✕ reste — c'est elle qui retire ») and 43c makes it a rule (« les lignes ne sont pas
    // supprimées, elles CONVERTISSENT »). Deriving it means the chip and the stepper are two
    // renderings of one number that moves with the cart, in both directions, for free.
    //
    // null = no ceiling known: untracked, oversell-allowed, or an entry persisted before `stock`
    // was captured at entry. Never guessed at — a guessed ceiling is the defect wearing a fix.
    // « |123=4|567=8| » → { 123: 4, 567: 8 }. Deliberately not JSON: the same string already feeds
    // the Liquid row caps, and reusing it keeps one source rather than two that can disagree.
    parseCartQty(raw) {
      var map = {};
      if (!raw) return map;
      raw.split('|').forEach(function (pair) {
        if (!pair) return;
        var bits = pair.split('=');
        if (bits.length !== 2) return;
        var id = parseInt(bits[0], 10);
        var qty = parseInt(bits[1], 10);
        if (!isNaN(id) && !isNaN(qty)) map[id] = qty;
      });
      return map;
    }

    capFor(id, entry) {
      if (!entry || entry.stock == null) return null;
      var cap = entry.stock - ((this.cartQtyMap || {})[parseInt(id, 10)] || 0);
      return cap < 0 ? 0 : cap;
    }

    // What this line will actually contribute to the commit. At cap 0 that is nothing — which is
    // why the « 6 » disappears with the stepper: it has become false, not merely unavailable.
    committableQty(id, entry) {
      var cap = this.capFor(id, entry);
      if (cap === null) return entry.qty;
      return Math.min(entry.qty, cap);
    }

    // Totals come from the basket, never from the pads currently rendered — see `selection` above.
    //
    // 🔴 43b — the bar counts what the COMMIT WILL PRODUCE, which is the 42b invariant applied to
    // the recap: a line whose cap has fallen to 0 contributes nothing, so it contributes nothing
    // here either. It still EXISTS (the panel renders it, converted, with its ✕) — it is only no
    // longer money the CTA can promise. A « réf » is likewise only counted when it can be added,
    // or the bar would advertise a reference the commit silently drops.
    totals() {
      var units = 0, cents = 0, selection = this.selection, products = {}, self = this;
      Object.keys(selection).forEach(function (id) {
        var entry = selection[id];
        var qty = self.committableQty(id, entry);
        if (qty <= 0) return;
        units += qty;
        cents += qty * entry.price;
        // Distinct références. Fall back to the variant id so a line with no product id still
        // counts as one rather than collapsing every such line into a single reference.
        products[entry.productId || ('v' + id)] = true;
      });
      return { units: units, cents: cents, refs: Object.keys(products).length };
    }

    // 🔴 SCROLL IS ANCHORED across the Grille ↔ Commande rapide switch (operator ruling).
    // Every card changes height when the pads appear or vanish, so the whole grid re-flows under a
    // fixed scrollTop and the buyer is dumped somewhere else in the catalogue — the further down
    // they were, the further they are thrown, because the delta compounds per row above them.
    //
    // The fix is to keep a REFERENCE CARD still rather than to keep the scroll offset still: pick
    // the card nearest the top of the viewport, note where it sits, switch mode, then re-scroll by
    // however far that same card moved. The products they were looking at stay where they were.
    setMode(fold) {
      // Anchor on the first card whose bottom is still below the viewport top — i.e. the topmost
      // card actually on screen, not merely the first in the DOM.
      var anchor = this.pads.find(function (p) { return p.getBoundingClientRect().bottom > 0; });
      var before = anchor ? anchor.getBoundingClientRect().top : null;

      this.classList.toggle('is-fold', fold);
      if (this.modeBtns.inline) { this.modeBtns.inline.classList.toggle('is-active', !fold); this.modeBtns.inline.setAttribute('aria-pressed', String(!fold)); }
      if (this.modeBtns.fold) { this.modeBtns.fold.classList.toggle('is-active', !!fold); this.modeBtns.fold.setAttribute('aria-pressed', String(!!fold)); }

      // Re-measure synchronously: the class swap has already been applied, so reading the rect here
      // forces the reflow and gives the post-switch position in the same frame — no visible jump.
      if (anchor && before !== null) {
        var after = anchor.getBoundingClientRect().top;
        var delta = after - before;
        if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
      }
    }

    onFoldClick(event) {
      var btn = event.target.closest('[data-op-fold-toggle]');
      if (!btn || !this.contains(btn)) return;
      var card = btn.closest('.oc-card');
      if (!card) return;
      var open = !card.classList.contains('is-expanded');
      card.classList.toggle('is-expanded', open);
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? (this.getAttribute('data-fold-close') || 'Fermer') : (this.getAttribute('data-fold-open') || 'Commander');
    }

    // From the basket, so add-all submits everything the buyer typed — including on pages and
    // filter states that are no longer rendered. Capped lines are excluded: they are not part of
    // what the commit produces (43b), only part of what the panel still shows.
    allSelected() {
      var selection = this.selection, self = this;
      return Object.keys(selection).map(function (id) {
        var qty = self.committableQty(id, selection[id]);
        if (qty <= 0) return null;
        return { variantId: parseInt(id, 10), qty: qty, price: selection[id].price };
      }).filter(Boolean);
    }

    recalc() {
      // Every mutation of the basket funnels through here — entry, the panel's steppers, both ✕,
      // the post-add clear — so this is the one place persistence has to be wired, and it cannot
      // be forgotten by a future path that forgets to call a save.
      this.saveSelection();
      var t = this.totals();
      var units = t.units, cents = t.cents;
      this.totalUnits = units;
      this.totalCents = cents;
      if (this.unitsEl) this.unitsEl.textContent = units;
      if (this.refsEl) this.refsEl.textContent = t.refs;
      if (this.refsPluralEl) this.refsPluralEl.textContent = t.refs > 1 ? 's' : '';
      if (this.unitsPluralEl) this.unitsPluralEl.textContent = units > 1 ? 's' : '';
      if (this.amountEl) this.amountEl.textContent = formatMoney(cents, this.moneyFormat);
      if (this.submitBtn) this.submitBtn.disabled = units === 0;

      this.renderBar(t);

      if (this.addedEl && units > 0) this.addedEl.hidden = true;
    }

    // ── The bar's states — A1 · A1p · A2 · A3 · A4, plus E (22d / 24b / 26a) ────────────────────
    // Only two facts decide the composition: is the SELECTION empty, and is the CART empty.
    //   both empty              → A1   bare empty track, no « Afficher », CTA disabled
    //   selection empty, cart   → A1p  gauge fills FROM THE CART, labelled in Afficher's free slot,
    //                                  message shown (a resting state with no entry in progress has
    //                                  the room), « Afficher » still absent, CTA disabled
    //   selection present       → A2/A3/A4 by threshold, which the gauge component decides
    // The A2/A3/A4 distinction is NOT made here on purpose: it is the gauge's grammar, and the one
    // place it is allowed to live is the shared component.
    renderBar(t) {
      if (!this.barEl) return;
      var units = t.units;
      var hasSel = units > 0;
      var hasCart = this.cartCents > 0;

      // 🔴 43c / CD-134 — « RIEN N'A PU ÊTRE AJOUTÉ », DERIVED, NOT FLAGGED.
      // The state is « she has composed a selection and NONE of it is addable » — which, now that
      // 43b caps every stepper at stock − cart, is simply « lines exist and the committable total
      // is 0 ». Deriving it from that instead of latching a flag after a failed submit is what
      // makes it correct in the cases CD actually named it for: a stale session, stock that moved,
      // a cart edited in another tab. It also means the buyer meets it BEFORE pressing the CTA
      // rather than after — CD's own residual-case note anticipates exactly this (« 43b's
      // arithmetic now prevents composing a null selection upstream »).
      //
      // Three of 43c's four rulings therefore hold by construction rather than by branch: the cart
      // does NOT open and Focal's lying « 10 articles ajoutés » is suppressed, because no request
      // is made at all; and chip E + « Réessayer » DIE for this case, because nothing failed.
      var hasLines = Object.keys(this.selection).length > 0;
      var nullAdd = hasLines && units === 0;
      this.barEl.classList.toggle('is-nulladd', nullAdd);
      // 15a: the gauge always measures the projected order, at rest as much as expanded — which is
      // exactly what 26a settled for A1p.
      // 🔴 The gauge measures the BASIS (pre-discount) + the selection. The money lines below use
      // `cartCents`. Two numbers, two jobs — see the init comment.
      var projected = this.cartBasisCents + t.cents;

      // A selection that empties out has nothing left to show, so the panel cannot stay open.
      // 🔴 A null-add selection is NOT empty: 43c is explicit that « les lignes ne sont pas
      // supprimées, elles CONVERTISSENT » — removing them would erase her input without her gesture
      // AND without the why. The panel stays open on the converted rows, which ARE the report.
      if (!hasSel && !nullAdd && this.panelOpen) this.setPanel(false);

      this.barEl.classList.toggle('is-a1p', !hasSel && hasCart && !nullAdd);

      // 🔴 AN EMPTY SELECTION HIDES THE BAR, EVERYWHERE — operator ruling 2026-08-13: « I want
      // sticky to be hidden when selection is empty. »
      //
      // This SUPERSEDES the 2026-08-09 ruling (« when selection empty, I want sticky to only appear
      // on collection pages ») — the same instinct, wider scope — and the `!hasCart` / `!this.gridEl`
      // terms are gone with it. Kept on record because the reasoning behind them was sound and a
      // future session must not "restore" it as a regression:
      //   · the grid gate existed so the empty bar showed where a selection could be BUILT;
      //   · `!hasCart` protected A1p — selection empty, cart not — which is CD 26a's whole subject
      //     and the first open question in OPERATOR-VALIDATIONS-OWED. **That question is now
      //     answered: hide it.** 26a's A1p is retired as a rendered state.
      //
      // ⚠️ THE COST, ACCEPTED KNOWINGLY: the gauge lives in this bar, so a buyer browsing with an
      // empty selection and a non-empty cart no longer sees their progress toward the minimum while
      // they shop. It returns the moment they select anything, and the cart page and drawer both
      // carry the same gauge. That trade is exactly what A1p existed to avoid, and it is the
      // operator's to make.
      //
      // A null-add selection is NOT empty (43c: « les lignes ne sont pas supprimées, elles
      // CONVERTISSENT ») — it stays visible, because it is a report the buyer has not read yet.
      //
      // Body clearance follows for free: `--ocg-bar-h` is published from the surface's MEASURED
      // height by a ResizeObserver, so a display:none bar publishes 0px and both
      // `body { padding-bottom: var(--ocg-bar-h) }` and the consent-open `calc()` that adds to it
      // collapse on their own. No second rule, and nothing to keep in sync.
      this.barEl.classList.toggle('is-sel-empty', !hasSel && !nullAdd);

      if (this.emptyEl) this.emptyEl.hidden = hasSel || nullAdd;
      if (this.totalEl) this.totalEl.hidden = !hasSel;

      // 🔴 43c §2 — ONE VOICE, IN THE BAR, and it takes the RECAP LINE of its slot. The 42b
      // invariant again: the slot says what the commit will produce — here, nothing. Plain text on
      // the band in #FFD2D6, never a pill: « ce n'est pas un habit d'erreur, c'est une
      // information ». (The paint is in the CSS; this only owns which slot speaks.)
      if (this.nullAddEl) {
        this.nullAddEl.hidden = !nullAdd;
        if (nullAdd) this.nullAddEl.textContent = this.nullAddMessage;
      }

      // « Afficher » is present as soon as a line exists — absent in A1 AND A1p (26a: the panel is
      // the SELECTION's surface; with an empty selection it would open on nothing). In the null-add
      // state the panel has plenty to show — the converted rows and her ✕ — so the way in stays.
      if (this.toggleEl) {
        this.toggleEl.hidden = !hasSel && !nullAdd;
        if (this.toggleLabelEl) {
          // 🔴 OPERATOR RULING 2026-08-01, overriding 24d: the « (n) » is DROPPED at both
          // breakpoints. 24d made the count desktop-only; the operator's read is that it does not
          // earn its place at all — « I'm not seeing the purpose of displaying (N), it's more
          // confusing than anything ». The recap immediately to its left already states the
          // selection in full (« 2 réf · 27 art. · 229,50 € HT »), so the count restated a number
          // the eye has just read, in a different unit, inside the control that opens the list.
          // The verb swap plus the chevron carry the state. Flagged to CD as a 24d amendment.
          this.toggleLabelEl.textContent = this.panelOpen ? this.labelHide : this.labelShow;
        }
      }

      // A1p's short cart label, in the slot « Afficher » leaves free. Not in the null-add state:
      // « Afficher » is present there, so the slot is taken — and 43c wants ONE voice, not two.
      if (this.cartRestEl) {
        var showRest = !hasSel && hasCart && !nullAdd;
        this.cartRestEl.hidden = !showRest;
        if (showRest) this.cartRestEl.textContent = this.fill(this.tplCartRest, {
          cart: formatMoney(this.cartCents, this.moneyFormat),
          ht: this.htSuffix
        });
      }

      // CD-64 — the cart line belongs to the EXPANDED state, and is never rendered on an empty
      // cart. « Panier vide » does not exist in any state: it only restated the selection total and
      // invited reading the selection as the cart.
      //
      // 🔴 CD-129 / 42b §8b — THE CART LINE TAKES THE RECAP'S SLOT; it no longer sits beneath it.
      // CD's rule is « le slot dit toujours ce que le commit produira ». With the panel open the
      // panel's own foot already states the selection 40px higher, so the bar's recap says it
      // twice — the recap therefore DROPS and the cart line rises into its place, and into its
      // STYLE (the type change is in the CSS), which is what makes this a promotion rather than a
      // reshuffle.
      //
      // The empty-cart branch is what makes the invariant hold instead of break: with no cart there
      // IS no cart line, so the recap STAYS — selection alone is exactly what the commit will
      // produce. The slot is never empty either way, which is the whole point. The recap/foot
      // redundancy is tolerated in that single case as the lesser evil against a slot that blinks.
      var showLine = hasSel && hasCart && this.panelOpen;
      if (this.cartlineEl) {
        this.cartlineEl.hidden = !showLine;
        if (showLine) this.cartlineEl.textContent = this.fill(this.tplCartline, {
          cart: formatMoney(this.cartCents, this.moneyFormat),
          total: formatMoney(projected, this.moneyFormat),
          ht: this.htSuffix
        });
      }
      // Ordering matters: this runs AFTER the `hidden = !hasSel` above, and drops the recap only
      // when the cart line is actually there to take over — the slot is never left dark.
      if (this.totalEl && showLine) this.totalEl.hidden = true;

      this.renderGauge(projected);
      if (this.panelOpen) this.renderPanel(t);
    }

    // 43c §4 — « rien à ajouter », the copy that stands in for a figure wherever a money slot has
    // nothing to state. Same string in the group subtotal and in the foot, so the two cannot drift.
    nothingToAdd() { return this.nullAddShort; }

    // Template fill for the settings-driven copy: "{cart}", "{total}", "{ht}", "{gap}", "{min}",
    // "{franco}", "{discount}", "{minstate}", "{lines}", "{units}". Same placeholder convention as
    // the Liquid side — the two renderers substitute into the same merchant strings.
    fill(template, values) {
      var out = template || '';
      Object.keys(values).forEach(function (key) {
        out = out.split('{' + key + '}').join(values[key]);
      });
      return out;
    }

    // Re-render the shared gauge from the live contract. This mirrors b2b-threshold-gauge.liquid
    // exactly — same three milestones, same re-base per attainment (CD-04 / 49b), same positional
    // fills (49c), same gate tense (49a), same 21a degrade. If you change one, change the other:
    // they are ONE component with two renderers, and the whole reason the contract is passed in
    // data-* attributes is so the NUMBERS and COPY only ever have one source.
    //
    // `totalCents` is the PROJECTION (cart + selection) — CD-03, one progression. `this.cartCents`
    // is consulted for exactly one thing: the minimum's tense, because the minimum is a GATE and
    // checkout is locked on the cart alone. 🔴 That is NOT a basis split (48a is withdrawn) — one
    // progression, one fill, one scale, plus one extra input used by one marker's state.
    renderGauge(totalCents) {
      if (!this.gaugeEl || !this.gauge) return;
      var g = this.gauge;

      // A milestone exists only if it is resolvable AND strictly above the one before it.
      var hasFranco = g.franco > g.min;
      var floor = hasFranco ? g.franco : g.min;
      var hasDiscount = g.discount > floor;
      var single = !hasFranco && !hasDiscount;
      var last = hasDiscount ? g.discount : (hasFranco ? g.franco : g.min);

      var minReached = g.min > 0 && totalCents >= g.min;
      var francoReached = hasFranco && totalCents >= g.franco;
      var allReached = g.min > 0 && totalCents >= last;

      // THE GATE (49a) — the cart ALONE. `gatePending` is « the projection has cleared the minimum
      // but the cart has not », the only condition under which the ✓ is withheld from the minimum.
      var gateOpen = g.min > 0 && this.cartBasisCents >= g.min;
      var gatePending = minReached && !gateOpen;

      // The chip needs BOTH: no live goal left, and the door actually open (49d). All-reached with
      // the door shut is a full track with a hollow minimum, never a chip — a chip announcing
      // « Minimum atteint » beside a disabled checkout is the contradiction round 27 removes.
      if (allReached && !single && gateOpen) {
        var chip = hasDiscount ? (hasFranco ? g.chipAll : g.chipDiscount) : g.chipBoth;
        this.gaugeEl.className = 'b2bg b2bg--bar b2bg--met';
        this.gaugeEl.innerHTML = '<span class="b2bg__chip">' + this.ok(chip) + '</span>';
        return;
      }

      // 🔴 ONE CONTINUOUS TRACK — the re-base is retired (D85). Two DIFFERENT numbers, and under
      // CD-04 they were the same value, which is how one variable used to serve both:
      //   · `scale` — the top milestone. Geometry: the fill's width and every marker position.
      //   · `next`  — the first milestone not yet reached. Words only: « encore {gap} pour … ».
      // Compute the gap off the scale and every message quotes the distance to the discount while
      // naming the franco. The Liquid twin carries the same warning.
      var scale = last;
      var next;
      if (!minReached) next = g.min;
      else if (hasFranco && !francoReached) next = g.franco;
      else next = last;

      var pct = scale > 0 ? Math.max(0, Math.min(100, (totalCents * 100) / scale)) : 0;
      var gap = Math.max(next - totalCents, 0);

      // Every milestone is drawn, earned or not — the road ahead is the point of a fixed scale.
      // Positions never move. Only milestones strictly below the scale are emitted here; the top
      // one is emitted at 100 %. `g.min < scale` is false exactly when the account is
      // single-milestone, and then the minimum IS the top marker — no special case.
      var showMin = g.min > 0 && g.min < scale;
      var minPct = showMin ? (g.min * 100) / scale : 0;
      var showFranco = hasFranco && g.franco < scale;
      var francoPct = showFranco ? (g.franco * 100) / scale : 0;

      var message;
      // 🔴 ORDER IS THE RULE — `single` MUST be tested before `allReached`, and the Liquid twin
      // carries the same warning. With one milestone `last === g.min`, so reaching the minimum
      // also satisfies `allReached`; the other way round a degraded account is told « Franco
      // atteint ✓ » for a franco it will never have (21a's exact prohibition — shipped and
      // reverted 2026-08-12, D73). Signed-out visitors are ALL on this branch, because both
      // resolvers need `customer` and the bar does not.
      if (totalCents <= 0) message = g.msgEmpty;
      else if (!minReached) message = g.msgBelow;
      else if (single) message = gatePending ? g.chipMinPending : g.chipMin;
      else if (allReached) message = hasDiscount ? g.msgAllPending : g.msgAllPendingFranco;
      else if (francoReached) message = g.msgDiscount;
      else if (hasFranco) message = g.msgFranco;
      else message = g.msgDiscountNoFranco;

      message = this.fill(message, {
        gap: formatMoney(gap, this.moneyFormat),
        // The gap keeps its cents (an amount to add); the THRESHOLD loses them — CD draws
        // « (180 € HT) », never « (180,00 €) ». Mirrors `b2b-euros` in the Liquid twin: whole
        // euros, U+00A0 before the sign, non-breaking so a figure never wraps mid-number.
        min: Math.round(g.min / 100).toLocaleString('fr-FR').replace(/\u202f|\s/g, '\u00a0') + '\u00a0€',
        franco: g.francoLabel,
        discount: g.discountLabel,
        minstate: gatePending ? g.minStatePending : g.minStateOn
      });

      // ONE FILL, one tone per surface (51b). 49c's positional ladder is retired, and with it two
      // of the three round-27 defects — both were ladder defects. A positional colour rule that
      // does not exist cannot misfire on a degraded account.
      var cls = 'b2bg b2bg--bar';
      if (single) cls += ' b2bg--single';
      if (minReached) cls += ' b2bg--min-reached';
      if (gatePending) cls += ' b2bg--gate-pending';

      // 🔴 Two marker states (D85): earned = filled green ✓; UNEARNED = hollow, white centre with
      // a green outline. NOT a restoration of 49a's withdrawn hollow pastille — that one was hollow
      // for an ALREADY-EARNED milestone whose gate was pending, and it stays withdrawn. This hollow
      // means not-yet-earned, and only a continuous track can produce it. Liquid twin has the full
      // reasoning; do not "correct" one without the other.
      // 🔴 THE TOOLTIP IS REBUILT HERE TOO — 53e « lecture 3 »: « identité · seuil · ✓ si acquis »,
      // one rule for every milestone, the ✓ the only carrier of state. This function is the TWIN of
      // the Liquid's, and the first build shipped without it: the server rendered tooltips, then
      // this renderer replaced the markers on the buyer's first quantity change and they vanished —
      // on the sticky bar, which re-renders immediately, they never appeared at all. Any marker
      // attribute added on one side MUST be added on the other.
      var self = this;
      // Same formatter the sentence's {min} uses above, and for the same reason: whole euros,
      // U+00A0 before the sign so a figure never wraps mid-number. Thresholds lose their cents.
      var euros = function (cents) {
        return Math.round(cents / 100).toLocaleString('fr-FR').replace(/\u202f|\s/g, '\u00a0') + '\u00a0\u20ac HT';
      };
      var tipText = function (label, cents) {
        return self.fill(self.gauge.tipFormat, { label: label, amount: euros(cents) });
      };
      // 🔴 THE MARKER AND ITS TOOLTIP ARE SEPARATE ELEMENTS — the tooltip is a sibling of the
      // track, not a child of the marker, so it can be positioned against the WHOLE gauge (51c).
      // Nested inside the marker its only containing block was the marker itself, so its offset
      // was measured from the dot and it landed inside the message line. Liquid twin carries the
      // full reasoning; the pairing is by `data-b2bg-mk` / `data-b2bg-tip-for`, and the hover is
      // re-linked in CSS with `:has()`.
      var mk = function (pctPos, earned, key, extra) {
        // The label carries the ✓ verbatim when earned (53e). Copy is merchant-set, so escape it.
        var label = self.esc(tips[key]) + (earned ? ' ✓' : '');
        return '<span class="b2bg__milestone' + (extra || '') + (earned ? '' : ' b2bg__milestone--todo') +
          '" style="left: ' + pctPos + '%" data-b2bg-mk="' + key + '" role="img" aria-label="' + label + '">' +
          (earned ? '✓' : '') + '</span>';
      };
      // The visible tooltip takes aria-hidden so the line is not announced twice.
      var tipEl = function (pctPos, earned, key, extra) {
        var inner = self.esc(tips[key]) + (earned ? ' <span class="b2bg__tipok">✓</span>' : '');
        return '<span class="b2bg__tip' + (extra || '') + '" data-b2bg-tip-for="' + key +
          '" style="left: ' + pctPos + '%" aria-hidden="true">' + inner + '</span>';
      };
      // 🔴 THE THIRD BRANCH. `top` was discount-else-franco with no case for NEITHER, so a
      // `single` gauge — every signed-out visitor, every unresolved zone — labelled its 100 %
      // marker with the franco at a value of ZERO: « Franco · 0 € HT ». Rule 21a forbids naming
      // a threshold we cannot honour, and 0 € is the worst one available.
      //
      // On `single` the top milestone IS the minimum (the min marker is suppressed precisely
      // because the two coincide), so the top tooltip must carry the minimum's name.
      //
      // This mirrors the identical fix in b2b-threshold-gauge.liquid. ONE COMPONENT, TWO
      // RENDERERS — they move together or the surfaces disagree, and they did: the Liquid fix
      // alone left the CART correct and the STICKY BAR still saying « Franco · 0 € HT ».
      var tips = {
        min: tipText(g.tipMin, g.min),
        franco: tipText(g.tipFranco, g.franco),
        top: hasDiscount
          ? tipText(g.tipDiscount, g.discount)
          : (single ? tipText(g.tipMin, g.min) : tipText(g.tipFranco, g.franco))
      };

      var html = '';
      if (message) html += '<p class="b2bg__message">' + this.ok(message) + '</p>';
      html += '<div class="b2bg__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '">';
      if (totalCents > 0) html += '<span class="b2bg__fill" style="width: ' + pct + '%"></span>';
      if (showMin) html += mk(minPct, minReached, 'min');
      if (showFranco) html += mk(francoPct, francoReached, 'franco');
      // The top milestone, always at 100 % — the old single-phase end-cap generalised, because
      // every account's goal now sits at the end of its own track. It names whatever the scale ENDS
      // on: for an account with no −10 % that is the franco, never a discount it will never have.
      html += mk(100, allReached, 'top', ' b2bg__milestone--top');
      html += '</div>';

      // The tooltips close the gauge, OUTSIDE the track — see the anchor note above.
      if (showMin) html += tipEl(minPct, minReached, 'min');
      if (showFranco) html += tipEl(francoPct, francoReached, 'franco');
      html += tipEl(100, allReached, 'top', ' b2bg__tip--top');

      this.gaugeEl.className = cls;
      this.gaugeEl.innerHTML = html;
    }

    // Merchant copy goes into innerHTML, so escape it first, then colour the ✓ glyph — CD colours
    // the tick, not the sentence, and on encre it has to become mint because #41AD7D fails contrast
    // on #353535 (26b). Escaping leaves the ✓ character untouched, so the swap is safe after it.
    ok(text) {
      var escaped = this.esc(text);
      return escaped.split('✓').join('<span class="b2bg__ok">✓</span>');
    }

    esc(text) {
      return String(text == null ? '' : text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    // ── The selection panel (17b / 24a / 24c) ──────────────────────────────────────────────────
    togglePanel() {
      this.setPanel(!this.panelOpen);
      // The cart line and the toggle's verb both belong to the open/closed state. setPanel itself
      // deliberately does NOT recalc — renderBar closes the panel when the selection empties, and
      // a recalc in there would re-enter renderBar.
      this.recalc();
    }

    // The panel slides on transform, 280ms (41c canonised it); the bar stays put and gains its
    // context lines in the same gesture (24d — fade + slide 150ms, built in the round-18 pass).
    //
    // 🔴 This no longer drives the open state itself. `.ocg__bar` IS a Focal <openable-element>, so
    // setting its `open` property is the whole mechanism: the theme adds/removes the `[open]`
    // attribute, CSS runs the slide off it, and Focal supplies the scrim click, the ✕
    // (`[data-action="close"]`), the focus trap and the page-scroll lock — 41a's 1b and 1c for free.
    //
    // What this REPLACED, and why it is worth the note: a hand-rolled transform + `transitionend`
    // listener + a 400ms timeout fallback + a `hidden` two-step. transitionend is not guaranteed
    // (it never fires if the element goes display:none mid-slide, which the cart drawer does), and
    // missing it left the wrapper laid out but invisible — a transparent ~290px band over the grid
    // swallowing clicks. The primitive has no such failure mode: there is no event to miss.
    //
    // The panel is now absolutely positioned above the bar surface (see the CSS), so a closed panel
    // contributes nothing to the bar's height and the `hidden` dance is unnecessary.
    // 🔴 HOW FAR THE PAGE MUST SCROLL UP FOR THE BAR TO RE-PIN. 0 when it is already pinned.
    //
    // `.ocg__bar` is `position: sticky; bottom: 0` inside <order-pad-collection>, whose box ends
    // before the footer — so in the last stretch of a collection page the bar UNPINS and rides up
    // with the page. MEASURED at 390×844 on /collections/jolies-culottes (doc 41 788px):
    //     scrollY 0…40 500 → barTop 702…646, panel ground 506 (correct)
    //     scrollY 40 944   → barTop 202, ground 138  ← the collapse
    //
    // The measurement is the DISTANCE, not a scroll threshold: how far the bar's bottom edge sits
    // above the viewport bottom is exactly how far the page has to come back up to re-pin it. No
    // page height, no footer height, no magic number — and it reads 0 the instant the bar is
    // pinned, at any scroll position, which a threshold could never do.
    // Clamped to the current scroll offset: the page cannot scroll above its own top, and without
    // the clamp the settle loop below would spin to its ceiling on a short page.
    barUnpinnedBy() {
      if (!this.barEl) return 0;
      var gap = window.innerHeight - this.barEl.getBoundingClientRect().bottom;
      if (gap <= 1) return 0; // sub-pixel tolerance: a fractional gap is not an unpinned bar
      var offset = window.scrollY || window.pageYOffset || 0;
      return Math.min(gap, offset);
    }

    // 🔴 OPENING RE-PINS THE BAR FIRST — operator ruling 2026-08-03, verbatim: « When clicking on
    // "Afficher" once having gone past order bar sticky, first scroll back up enough to get bar
    // sticky again, then have panel open up. »
    //
    // The sequencing is the point, not a nicety. The panel is anchored to the bar (`bottom: 100%`)
    // and `sizePanel()` measures the room ABOVE it, so opening first would measure against the
    // unpinned position and cap the ground at ~138px — the collapse this exists to prevent. And it
    // cannot be reordered later: `onPanelToggled` runs `setPageLock(true)` on open, after which the
    // page cannot be scrolled at all.
    //
    // Applied in `setPanel`, not in the « Afficher » click handler, so every route in — the toggle,
    // the drawer's « reprendre la sélection » — gets the same behaviour.
    setPanel(open) {
      if (!this.barEl) return;

      // A second « Afficher » while a re-pin is in flight is the SAME request, not a new one — let
      // the pending sequence finish rather than opening at the position it is still correcting.
      // A close, by contrast, must cancel it, or the settle loop would re-open the panel a moment
      // after the buyer shut it. The sequence number is what makes the cancellation reach a
      // callback already queued: it checks the number it was born with.
      if (this._repinSeq == null) this._repinSeq = 0;
      if (this._repinning) {
        if (open) return;
        this._repinning = false;
        this._repinSeq++;
      }

      if (open) {
        var lift = this.barUnpinnedBy();
        if (lift > 0) {
          this._repinning = true;
          var seq = ++this._repinSeq;
          var self = this;
          // « Instant » under reduced-motion: the correction is a jump the buyer did not ask for,
          // which is exactly the class of movement that setting turns off.
          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollBy({ top: -lift, behavior: reduce ? 'instant' : 'smooth' });
          // Wait for the scroll to SETTLE rather than for a fixed delay — smooth scrolling has no
          // duration we are allowed to assume, and a fixed timeout can only ever be lucky (the same
          // trap the drawer's 260ms scroll-to-line hit). Re-measuring is cheap and self-terminating:
          // `barUnpinnedBy` returns 0 the moment the bar is pinned OR the page reaches its top.
          var deadline = Date.now() + 1200;
          var settle = function () {
            if (seq !== self._repinSeq) return; // cancelled by a close — do not open behind it
            if (self.barUnpinnedBy() === 0 || Date.now() > deadline) {
              self._repinning = false;
              self.applyPanelState(true);
              return;
            }
            window.requestAnimationFrame(settle);
          };
          window.requestAnimationFrame(settle);
          return;
        }
      }

      this.applyPanelState(open);
    }

    applyPanelState(open) {
      if (!this.barEl) return;
      // Guard: openable-element upgrades asynchronously. Before it does, `open` is a plain
      // expando and the attribute never lands, so mirror it by hand — the CSS keys off the
      // attribute either way.
      if ('open' in this.barEl && this.barEl.constructor !== HTMLElement) {
        this.barEl.open = open;
      } else {
        this.barEl.toggleAttribute('open', open);
        this.onPanelToggled();
      }
    }

    // 🔴 The panel must never be taller than the room actually left between the sticky chrome above
    // it and the bar below it. It is anchored to the bar's top edge and grows UPWARD, so when the
    // header was on screen (scrolling back up re-reveals it) the panel's own head — the title, the
    // badge and the ✕ — slid underneath it and became unreachable: the only way out was « Masquer ».
    //
    // MEASURED, not assumed. The header's height is a live number here (Focal hides and re-shows it
    // on scroll, and the announcement bar may or may not be present), so a CSS constant would be
    // wrong half the time. Anything sticky at the top of the viewport is found by hit-testing the
    // top edge, which also covers the collection sub-header without naming it.
    // 🔴 NOT `position: fixed` on <body>, the usual modal lock — it would break this surface.
    // The bar is `position: sticky; bottom: 0` in the page flow; fixing the body removes the
    // scrollport it sticks against, so the bar would settle at the bottom of the CONTENT instead of
    // the viewport and disappear. The lock therefore has to happen without touching body layout.
    //
    // So: intercept the gesture instead. A touch/wheel inside the panel is allowed only as far as
    // the rows can actually absorb it; everything else — including every gesture on the scrim — is
    // cancelled. That covers the operator's case exactly (a selection too short to scroll, where
    // `overscroll-behavior: contain` cannot help because the gesture never belongs to the rows at
    // all) as well as the ordinary chain at the ends of a long list.
    setPageLock(on) {
      if (on === !!this._locked) return;
      if (on) {
        var self = this;
        this._onChain = function (event) {
          var rows = self.querySelector('[data-oc-panel-rows]');
          // Anything outside the rows — the scrim, the head, the footer, the bar — never scrolls.
          if (!rows || !rows.contains(event.target)) { if (event.cancelable) event.preventDefault(); return; }
          var canScroll = rows.scrollHeight > rows.clientHeight + 1;
          if (!canScroll) { if (event.cancelable) event.preventDefault(); return; }
          // Scrollable, but already at an end and pushing further: `overscroll-behavior: contain`
          // handles this in modern browsers; cancelling is the belt-and-braces for the rest.
          var dy = event.deltaY !== undefined ? event.deltaY : 0;
          var atTop = rows.scrollTop <= 0;
          var atEnd = rows.scrollTop + rows.clientHeight >= rows.scrollHeight - 1;
          if ((atTop && dy < 0) || (atEnd && dy > 0)) { if (event.cancelable) event.preventDefault(); }
        };
        // Non-passive on purpose: a passive listener cannot preventDefault, which is the whole job.
        this.barEl.addEventListener('touchmove', this._onChain, { passive: false });
        this.barEl.addEventListener('wheel', this._onChain, { passive: false });
        this._locked = true;
      } else {
        if (this._onChain && this.barEl) {
          this.barEl.removeEventListener('touchmove', this._onChain);
          this.barEl.removeEventListener('wheel', this._onChain);
        }
        this._onChain = null;
        this._locked = false;
      }
    }

    sizePanel() {
      if (!this.panelEl || !this.barEl) return;
      var rowsEl = this.querySelector('[data-oc-panel-rows]');
      var cardEl = this.querySelector('.ocg__panel-card');
      if (!rowsEl || !cardEl) return;

      var barRect = this.barEl.getBoundingClientRect();
      var barTop = barRect.top;
      var topOccupied = 0;
      // Probe the very top of the viewport: whatever paints there is what would clip the panel.
      // 🔴 SKIP OUR OWN UNIT — the scrim is `position: fixed; inset: 0`, so while the panel is open
      // it IS the topmost element at that point and its bottom is the full viewport height. An
      // earlier version measured ITSELF, topOccupied became the viewport height, and the cap
      // collapsed to its floor. Anything inside the bar cannot be what clips the bar.
      //
      // 🔴 …BUT SKIPPING IT VIA `elementFromPoint` + `parentElement` MADE THE PROBE DEAD.
      // Measured on the dev theme 2026-08-03 at 390: with the panel open, `elementFromPoint(w/2, 4)`
      // returns the scrim, and walking its ANCESTORS only ever reaches the bar and the page — never
      // Focal's sticky header, which is a SIBLING branch. So `topOccupied` was 0 in every open
      // state, the guard the whole loop exists for never fired, and the panel was free to slide
      // under the header whenever the room was tight (card top measured 132 against a header
      // occupying 0–56 — clear by luck, not by construction).
      // `elementsFromPoint` (plural) returns the whole hit stack rather than one branch, so the
      // header is actually seen. Same skip rule, applied per stack entry.
      var stack = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(Math.round(window.innerWidth / 2), 4)
        : [document.elementFromPoint(Math.round(window.innerWidth / 2), 4)];
      for (var si = 0; si < stack.length; si++) {
        var node = stack[si];
        if (!node || node === document.body || node === document.documentElement) continue;
        if (this.barEl.contains(node)) continue;
        var cs = window.getComputedStyle(node);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
        var r = node.getBoundingClientRect();
        // 🔴 SKIP FULL-VIEWPORT OVERLAYS — ANY unit's, not just our own. `topOccupied` means
        // « chrome pinned at the top of the viewport that would clip the panel ». A node covering
        // the WHOLE viewport height is a modal scrim, not chrome: capping against it leaves no room
        // at all and can only ever produce the 104px floor. The skip above already encodes exactly
        // this for our OWN scrim (`.ocg__scrim`, fixed inset 0) via `barEl.contains`; this
        // generalises it, which is what stops the CART DRAWER's `.drawer__overlay` (fixed, top 0,
        // bottom = innerHeight, and still hit-testable throughout its 600ms close) from collapsing
        // the panel opened by « reprendre la sélection ». Belt to that route's braces: the click
        // handler waits the drawer out, and this makes a stray full-screen overlay unable to do it
        // again. Real chrome — Focal's header, the facet bar — is 56–64px and untouched by this.
        if (r.top <= 0 && r.bottom >= window.innerHeight - 1) continue;
        if (r.top <= 4 && r.bottom > topOccupied) topOccupied = r.bottom;
      }

      // 🔴 Cap the ROWS, not the outer panel. The rows are the only scrolling element; capping the
      // panel and trusting flex to pass the shrink down did NOT work — flex shrank the rows' parent
      // to 60px while the rows kept their own 506px max-height, so they overflowed it and painted
      // over the footer. That was the second half of the 390 breakage. Measuring what the rows may
      // occupy is direct and cannot desynchronise from what actually scrolls.
      var chrome = cardEl.getBoundingClientRect().height - rowsEl.getBoundingClientRect().height;
      var available = barTop - topOccupied - 12 - chrome;
      // Never below two rows: past that the panel is unusable, and overflowing beats vanishing.
      this.panelEl.style.setProperty('--ocg-panel-rows-max', Math.max(104, available) + 'px');

      // 🔴 45d — THE 58vh / min-240 CAP NOW INCLUDES THE FOOT, so the SCROLL ZONE is
      // « 58vh − hauteur du pied ». The measured branch above needs nothing: the foot left the
      // scroller, so it is already inside `chrome` (card − rows) and `available` subtracts it by
      // construction. Only the VIEWPORT branch — max(240px, 58vh), a pure CSS number — has no way
      // to know the foot's height, so it is published here and subtracted in the stylesheet.
      // Measured, not assumed: the foot's height is type + padding at two breakpoints (~34px
      // desktop, ~32 at 390) and a hardcoded constant would drift the day either changes.
      var footEl = this.querySelector('[data-oc-panel-foot]');
      this.panelEl.style.setProperty(
        '--ocg-panel-footh',
        (footEl ? Math.round(footEl.getBoundingClientRect().height) : 34) + 'px'
      );

      // ✅ ADDRESSED 2026-08-04, and NOT by loosening this floor. The one state in which this cap
      // collapsed at 390 is now handled in `setPanel`, which re-pins the bar BEFORE opening (see
      // `barUnpinnedBy`), so the panel never measures against an unpinned bar in the first place.
      // The arithmetic below is unchanged and stays correct for the geometry it is given.
      // The measurements that diagnosed it are kept:
      // `.ocg__bar` is `position: sticky; bottom: 0` inside `<order-pad-collection>`, whose box ends
      // before the footer. In the last ~450px of a collection page the bar therefore UNPINS from the
      // viewport bottom and rides up with the page, and since the panel is anchored to it
      // (`bottom: 100%`) the room above it genuinely shrinks with it. Measured on the dev theme
      // 2026-08-03, 390×844, /collections/jolies-culottes (doc 41 788px):
      //     scrollY 0 … 40 000  → barTop 702, cap 638 → ground 506 (= 60vh, correct)
      //     scrollY 40 500      → barTop 646, cap 582 → ground 506 (still correct)
      //     scrollY 40 944 (end)→ barTop 202, cap 138 → ground 138  ← the severe shrink
      // The arithmetic above is RIGHT for that geometry — there really is only 202px above the bar.
      // Two ways out were available: make the bar `fixed` (or give it a taller sticky containing
      // block), which changes a composition CD drew; or scroll the page back to where the bar is
      // pinned before opening. The operator ruled the second, verbatim: « first scroll back up
      // enough to get bar sticky again, then have panel open up. » Built in `setPanel`.
      // ⚠️ Do NOT "fix" the residual by loosening the 104px floor: that reopens the shrink-and-clip
      // failure the cap exists to prevent. The floor still guards the case where the buyer scrolls
      // the bar out of pin WHILE the panel is open (this method re-runs on scroll), which the
      // re-pin cannot reach because the page is locked by then.
    }

    // Single sink for the open/closed state, driven by Focal's own events rather than by whoever
    // called setPanel — so a close via the scrim, the ✕, Esc or the focus trap lands here exactly
    // like a close via the toggle. That is the point of adopting the primitive: one state path.
    onPanelToggled() {
      if (!this.barEl) return;
      var open = this.barEl.hasAttribute('open');
      if (this.panelOpen === open) return;
      this.panelOpen = open;
      if (this.toggleEl) this.toggleEl.setAttribute('aria-expanded', String(open));
      // 🔴 LOCK THE PAGE SCROLL while the panel is open — 41a specifies « scroll page verrouillé »
      // and it was never built. I had assumed <openable-element>'s focus trap covered it, but its
      // `preventScroll` only stops the browser scrolling to a focused node; it does not lock the
      // page. The symptom the operator hit: with a selection too short to scroll, dragging inside
      // the panel scrolled the CATALOGUE behind it. (`overscroll-behavior: contain` on the rows
      // cannot help there — with nothing to scroll, the gesture never belongs to the rows at all.)
      //
      // position:fixed on <body> rather than overflow:hidden, which iOS Safari ignores. The scroll
      // offset is parked on the body's own `top` and restored on close, so the page does not jump.
      this.setPageLock(open);

      // Size it the moment it opens, and keep it sized while it stays open: the header can appear
      // or disappear under the buyer's own scrolling with the panel already up.
      if (open) {
        this.sizePanel();
        if (!this._sizeBound) {
          // 🔴 rAF-THROTTLED, and inert while the panel is closed. sizePanel hit-tests the viewport
          // and reads computed styles — running that raw on every scroll event would put a forced
          // synchronous layout in the scroll path of a page carrying 120 cards and ~2900 rows,
          // i.e. it would CAUSE the jank it exists to prevent. One measurement per frame, max.
          var selfSz = this;
          this._sizeBound = function () {
            if (!selfSz.panelOpen || selfSz._sizePending) return;
            selfSz._sizePending = true;
            window.requestAnimationFrame(function () {
              selfSz._sizePending = false;
              if (selfSz.panelOpen) selfSz.sizePanel();
            });
          };
          window.addEventListener('resize', this._sizeBound);
          window.addEventListener('scroll', this._sizeBound, { passive: true });
        }
      }
      this.recalc();
    }

    // 🔴 41a — GROUPED BY PRODUCT, on the cart's (40a) anatomy. Replaces 17c and amends 17b.
    //
    // The panel used to emit one flat row per VARIANT, so three sizes of one colourway read as
    // three unrelated lines. CD granted the grouping in round 18: a product HEADER row (56px —
    // thumb, name, print · unit price, and on the right the group subtotal over « n tailles ·
    // n articles »), then one SIZE row per variant (52px — size + SKU in a column that hangs under
    // the thumb, stepper centred, line subtotal right).
    //
    // The header is CLICKABLE → that product's order-pad, exactly as in the cart.
    //
    // ONE anatomy at both breakpoints, where 17b/17c were two compositions: the grouping IS the
    // composition, and CD amended the desktop frame by note rather than redrawing it. Since the
    // operator's 2026-08-01 ruling put the ✕ on both breakpoints too, nothing here branches on
    // width any more — the only breakpoint differences left are CSS (type scale, sheet radius).
    renderPanel(t) {
      if (!this.panelTableEl) return;
      var self = this;
      var selection = this.selection;
      var ids = Object.keys(selection);

      // 🔴 CD-128 CLOSED BY 42b §8a — the foot speaks the BAR'S grammar: « N réfs · N articles »,
      // at both breakpoints. This is a SEMANTIC change, not a word swap: it used to count
      // `ids.length`, i.e. distinct SIZE ROWS, so the foot said « 18 lignes » under a bar 40px
      // below saying « 6 réfs » — two units for one selection, which is what the operator flagged.
      // A « réf » is a distinct PRODUCT, exactly as the bar's recap counts it, so the foot now
      // reads `t.refs`. 42a's own drawing confirms the reading: three product groups over six size
      // rows renders « 3 réfs · 49 articles ».
      //
      // Pluralise by count — the two words are their own placeholders precisely so this is
      // possible. Never « réf(s) »: the firm rule is paired forms selected by count.
      var footerText = this.fill(this.tplPanelFooter, {
        lines: t.refs,
        lineword: this.panelLineWord + (t.refs > 1 ? 's' : ''),
        units: t.units,
        unitword: this.articleWord + (t.units > 1 ? 's' : '')
      });
      // 43c — ONE voice in the foot too. « 0 réfs · 0 articles » beside « rien à ajouter » would be
      // the same information twice, once in a form that reads as a miscount.
      if (Object.keys(selection).length > 0 && t.units === 0) footerText = '';
      // 17b — the total aligns on the money axis and carries no « HT » of its own.
      // 43c §4 — « le pied likewise : rien à ajouter, sans chiffre ». No figure at all, rather than
      // a « 0,00 € », which would read as a price and invite pressing the CTA.
      var footNull = Object.keys(selection).length > 0 && t.units === 0;
      var totalText = footNull ? this.nothingToAdd() : formatMoney(t.cents, this.moneyFormat);

      // Group the flat basket by product, PRESERVING first-seen order so a group does not jump
      // when a size is added to it. Object key order is insertion order for string keys, which is
      // what the basket already relies on elsewhere.
      var groups = [];
      var byProduct = {};
      ids.forEach(function (id) {
        var entry = selection[id];
        // A missing productId would silently collapse every such line into one group, so fall back
        // to the variant id — worst case the line renders as its own group, which is honest.
        var key = entry.productId || ('v' + id);
        if (!byProduct[key]) {
          byProduct[key] = { key: key, entry: entry, lines: [], units: 0, commit: 0, cents: 0 };
          groups.push(byProduct[key]);
        }
        var g = byProduct[key];
        g.lines.push({ id: id, entry: entry });
        // 🔴 TWO counts, deliberately. `units` is what she SELECTED and feeds « n tailles ·
        // n articles » — that line describes her selection and must not shrink because the cart
        // moved. `cents` and `commit` are what the COMMIT will produce (42b's invariant), so a
        // capped line contributes nothing to the money. When the two disagree, the chips in the
        // rows are what shows the reader where.
        g.units += entry.qty;
        var gq = self.committableQty(id, entry);
        g.commit += gq;
        g.cents += gq * entry.price;
      });

      // 🔴 Sizes list in the PRODUCT'S OWN VARIANT ORDER, not in the order they were typed.
      // Entry order is whatever the buyer happened to click, so « 2XL, XL, L, XS » was normal —
      // and the panel and the cart disagreed because each fell back to a different accident.
      // `pos` is Shopify's variant position, captured at entry: no size table to maintain, no
      // alphabetical sort (which would put 2XL before S), and it survives any size vocabulary.
      // Entries stored before this shipped have pos 0; they sort first as a group and keep their
      // relative order, rather than being dropped or throwing.
      groups.forEach(function (g) {
        g.lines.sort(function (a, b) { return (a.entry.pos || 0) - (b.entry.pos || 0); });
      });

      // 🔴 PRODUCTS list MOST-RECENTLY-ADDED FIRST (operator ruling). Insertion order put the
      // oldest product at the top, so the line a buyer had just typed appeared at the BOTTOM of a
      // list they then had to scroll to see — the panel opened on the least relevant thing.
      // Only the product ORDER reverses: sizes inside a product keep the variant order above,
      // because that is a property of the product, not of when it was touched.
      groups.reverse();

      var body = groups.map(function (g) {
        var head = g.entry;
        var thumb = head.image
          ? '<img class="ocg__panel-thumb" src="' + self.esc(head.image) + '" alt="" loading="lazy" width="28" height="42">'
          : '<span class="ocg__panel-thumb"></span>';

        // « n tailles · n articles » — both words pluralised by count, never « taille(s) ».
        var groupSummary = self.fill(self.tplPanelGroup, {
          sizes: g.lines.length,
          sizeword: self.panelSizeWord + (g.lines.length > 1 ? 's' : ''),
          units: g.units,
          unitword: self.articleWord + (g.units > 1 ? 's' : '')
        });

        // The print and the unit price share one sub-line — « Dalida · 7,80 € HT/u ». The two
        // title parts get their own elements so §3's 3px/2px gap is a rule, not a margin guess.
        // The suffix is the UNIT-price one (« HT/u »), not the bar's plain « HT »: 41a and 40a both
        // redraw « /u » in exactly this position, where a unit price sits inline beside subtotals
        // and has to be told apart from them. Narrower than CD-27, which dropped « /u » site-wide —
        // the later, more specific frame governs. Flagged to CD.
        var sub = (head.print ? self.esc(head.print) + ' · ' : '') +
          formatMoney(head.price, self.moneyFormat) + ' ' + self.esc(self.unitSuffix || self.htSuffix || '');

        // 41a — the header is clickable, to this product's order-pad. An <a> rather than a JS
        // handler so it is a real link: middle-click, ⌘-click and the status bar all behave.
        var headTag = head.url ? 'a' : 'div';
        var headAttrs = head.url ? ' href="' + self.esc(head.url) + '"' : '';

        var headRow =
          '<' + headTag + ' class="ocg__panel-ghead"' + headAttrs + '>' +
            thumb +
            '<div class="ocg__panel-id">' +
              '<div class="ocg__panel-name">' + self.esc(head.name) + '</div>' +
              '<div class="ocg__panel-print">' + sub + '</div>' +
            '</div>' +
            '<div class="ocg__panel-gtot">' +
              // 43c §4 — a group with nothing addable states « rien à ajouter » in place of its
              // subtotal. Per GROUP, not per panel: a product that is fully in the cart says so
              // even while the rest of the selection is live, and the panel-wide null case falls
              // out of the same rule rather than needing a second one.
              (g.commit === 0
                ? '<div class="ocg__panel-gsum ocg__panel-gsum--none">' + self.esc(self.nothingToAdd()) + '</div>'
                : '<div class="ocg__panel-gsum">' + formatMoney(g.cents, self.moneyFormat) + '</div>') +
              '<div class="ocg__panel-gmeta">' + self.esc(groupSummary) + '</div>' +
            '</div>' +
            // 🔴 OPERATOR RULING 2026-08-01: the ✕ now renders at BOTH breakpoints, overriding
            // 17c's desktop-only rule. 17c ruled it out at 390 as too small a target beside the
            // stepper — but that was written against the FLAT panel, where the ✕ competed with a
            // 64px row's stepper and price. CD's own grouping freed the room: the product header is
            // its own 56px row with nothing on the right but the subtotal. Wording is the GROUP's,
            // never the line's — the two were indistinguishable on hover.
            '<button type="button" class="ocg__panel-remove ocg__panel-remove--group" ' +
              'data-oc-remove-group="' + self.esc(g.key) + '" ' +
              'title="' + self.esc(self.panelRemoveGroupLabel) + '" ' +
              'aria-label="' + self.esc(self.panelRemoveGroupLabel) + '">✕</button>' +
          '</' + headTag + '>';

        // Read ONCE per render, not per row: the flag is a body class stamped by theme.liquid from
        // the `b2b_show_sku` theme setting, which cannot change between two rows of one paint.
        var skuOn = document.body.classList.contains('b2b-sku-on');

        var sizeRows = g.lines.map(function (line) {
          var entry = line.entry;
          var id = line.id;

          // 🔴 44b — THE STATE FOLLOWS THE STEPPER, NOT THE SURFACE.
          // « Le tag ne vient pas de la carte Commande rapide — il vient du STEPPER. » 43b's rule is
          // written on the CONTROL: cap = stock − cart, and at 0 the stepper cedes its place to the
          // tag. This row HAS a stepper, offering the same size, obeying the same cap — so leaving
          // it alive at « 6 » would be exactly the lie round 20 buried: a control promising an
          // addition the commit cannot honour. The 42b invariant (« le slot dit ce que le commit
          // produira ») holds for every ROW, not only for the foot.
          //
          // What the tag does EXTRA here: in the card it warns BEFORE input; in the panel it is the
          // only useful lever AFTER — nothing in the panel can unblock the row, only the cart can,
          // so the chip-door leads to the one place the action exists. The row ✕ stays: removing
          // the line remains her gesture.
          var lineCap = self.capFor(id, entry);
          if (lineCap === 0) {
            var inCart = (self.cartQtyMap || {})[parseInt(id, 10)] || 0;
            var chipTitle = (self.allinTitle || '').split('{n}').join(inCart);
            // 🔴 THE TAG TAKES THE STEPPER ZONE ONLY — operator ruling 2026-08-06, of the LINE
            // TOTAL: « it should always be visible ». It used to span stepper AND total, so a row
            // caught up by the cart lost the one number a wholesale line must never hide. What has
            // become meaningless in this state is the STEPPER — nothing in the panel can unblock
            // the row, only the cart can, which is what the chip-door is for. The money has not
            // become meaningless: the line is still worth what it is worth.
            // So the chip sits where the stepper sat, and `.ocg__panel-line` returns on the money
            // axis, aligned with every subtotal above it and the foot below it.
            // 🔴 SKU settings-gated, default OFF — `b2b_show_sku`, read off the body class the
            // stylesheets key on, so there is exactly ONE flag and no second copy to drift.
            // See DECISIONS.md D1 (amended 2026-08-04).
            // Same widening rule as the action-line row below — see the note there.
            var allinSize = String(entry.size || '');
            var allinWide = allinSize.indexOf('/') !== -1 || allinSize.length > 6;
            return '<div class="ocg__panel-row is-allin' + (allinWide ? ' is-wide' : '') + '">' +
              '<div class="ocg__panel-size">' + self.esc(entry.size) + '</div>' +
              (skuOn ? '<div class="ocg__panel-sku">' + self.esc(entry.sku || '') + '</div>' : '') +
              '<button type="button" class="ocg__panel-allin" data-op-allin-chip ' +
                'data-op-product-url="' + self.esc(entry.url || '') + '" ' +
                'data-op-cart-url="' + self.esc(self.cartUrl) + '" ' +
                'title="' + self.esc(chipTitle) + '" aria-label="' + self.esc(chipTitle) + '">' +
                '<span class="ocg__panel-allin-tick" aria-hidden="true">✓</span>' +
                self.esc(self.allinLabel) +
              '</button>' +
              '<span class="ocg__panel-line">' + formatMoney(entry.qty * entry.price, self.moneyFormat) + '</span>' +
              '<button type="button" class="ocg__panel-remove" data-oc-remove="' + self.esc(id) + '" ' +
                'title="' + self.esc(self.panelRemoveLabel) + '" ' +
                'aria-label="' + self.esc(self.panelRemoveLabel) + '">✕</button>' +
            '</div>';
          }

          // Focal's own <quantity-selector> + <input-number>, the SAME markup the collection cards
          // use. The panel previously hand-wrote bare buttons, which is why the two steppers felt
          // different — and why their glyphs sat 12px off-centre (the theme resets buttons to
          // text-align:start; quantity-selector__button already handles that).
          //
          // 🔴 The buttons carry NO step handler of ours. <quantity-selector> binds its own
          // first-child/last-child buttons to stepDown/stepUp and dispatches `change` — so keeping
          // our old `data-oc-step` click handler alongside it would apply every press TWICE. The
          // input's `change` event is the single write path (onPanelInput), exactly as the
          // collection cards already work.
          // 🔴 OPERATOR RULING — at qty 1 the « − » becomes a TRASH icon, because at 1 the only
          // thing decrementing can do is remove the line. Showing « − » there promises a decrement
          // and delivers a deletion. It must also swap BACK when a decrease returns the line to 1,
          // which is why this is computed from entry.qty on every render rather than set once.
          // Same rule will apply in the cart (40b) — one behaviour, two surfaces.
          // NB: this button is <quantity-selector>'s first child, so the primitive ALREADY binds it
          // to stepDown — 1 → 0 → `change` → onPanelInput deletes the line. So the trash state is a
          // pure glyph/label swap with NO handler of ours: adding `data-oc-remove` here would fire
          // our removal AND the primitive's step, on a node the first one had already detached.
          var atOne = entry.qty <= 1;
          var downBtn = atOne
            ? '<button type="button" class="quantity-selector__button ocg__panel-trash" ' +
                'title="' + self.esc(self.panelRemoveLabel) + '" ' +
                'aria-label="' + self.esc(self.panelRemoveLabel) + '">' + self.trashIcon() + '</button>'
            : '<button type="button" class="quantity-selector__button" aria-label="−">−</button>';
          // 🔴 46e — THE PANEL'S STEPPER GETS THE SAME CEILING AS EVERY OTHER SURFACE.
          // The panel already knew its cap — `capFor` above converts a row to the 43b tag at cap 0
          // — but between 1 and the cap the + was live and uncapped, so a buyer could climb past
          // available stock in the one place the whole selection is reviewed. That is the same
          // « control promising an addition the commit cannot honour » the 42b invariant forbids,
          // one notch below the state it was already catching.
          //
          // `max` is what actually enforces it (input.stepUp() will not pass it, and the existing
          // clamp pass corrects a typed value). `aria-disabled` + the bubble are what make it
          // VISIBLE — never `disabled`, which would kill the pointer events the bubble needs.
          // A NULL cap means untracked stock: no ceiling exists, so all three attributes are
          // withheld and the stepper stays exactly as it was.
          var capped = lineCap !== null;
          var atCeiling = capped && entry.qty >= lineCap;
          var stepper =
            '<quantity-selector class="ocg__panel-step quantity-selector' + (atOne ? ' is-at-one' : '') + '">' +
              downBtn +
              '<input type="number" is="input-number" class="quantity-selector__input ocg__panel-qty" ' +
                'inputmode="numeric" autocomplete="off" min="0" value="' + entry.qty + '" ' +
                (capped ? 'max="' + lineCap + '" ' : '') +
                'data-oc-qty data-oc-variant="' + self.esc(id) + '" ' +
                'aria-label="' + self.esc(entry.name + ' ' + entry.size) + '">' +
              '<button type="button" class="quantity-selector__button" aria-label="+"' +
                (capped ? ' data-b2b-ceiling data-ceiling-label="' + self.esc(self.ceilingLabel) + '"' : '') +
                (atCeiling ? ' aria-disabled="true"' : '') +
                '>+</button>' +
            '</quantity-selector>';

          // 🔴 CD-127 CLOSED BY 42a — the desktop panel is DRAWN at last, and the columnar
          // furniture is RETIRED, not restored. CD's ruling, verbatim in intent: grouping moves
          // the panel toward the 40a CART, not toward columnar 17b — « l'en-tête de groupe nomme
          // déjà le produit, et le panier n'a pas d'en-tête de colonnes ».
          //
          // So the row is ONE composition at both breakpoints: an indented size block, stepper
          // centred, line total on the money axis, ✕ last. Desktop differs only in SCALE and in the
          // indent/track widths — never in structure. The `.ocg__panel-unit` column is gone from
          // the markup (not merely hidden): 42a §3 deletes it because the group header's sub-line
          // « Dalida · 7,80 € HT/u » already carries the unit price, and « la redondance lisait
          // comme une erreur ».
          //
          // 🔴 SKU settings-gated, default OFF (`b2b_show_sku` → body class `b2b-sku-on`, read once
          // above). OFF the row is a SINGLE line at both breakpoints and the CSS drops the second
          // grid row with it — no zero-height track left behind. ON, the CSS restores 43e's own
          // composition: a full-width SKU line under the action line at 390, and the SKU under the
          // size inside a 156px block at 1440 (43e is a 390 ruling: « Desktop UNCHANGED »).
          // Size and SKU stay SIBLINGS rather than nested — the row is a grid, and keeping them
          // separately addressable is what lets each breakpoint place them without touching this
          // markup. See DECISIONS.md D1/D2 as amended 2026-08-04.
          // 43e's `is-wide` survives on its own merit: a double-size variant (« 4XL / 4XL ») still
          // needs 110 instead of 82 on the action line, and only the CONTENT knows.
          // 🔴 The trigger was « contains a slash » — written when the only wide label was a
          // double size (« 4XL / 4XL »). Since 2026-08-25 the catalogue also holds real one-size
          // option values (« Taille unique », « 90 x 170 cm ») that are LONGER than that and carry
          // no slash, so they wrapped. Keyed on LENGTH as well, matching the same > 6 rule the
          // cart templates use, so one definition of « wide » governs every surface.
          var sizeStr = String(entry.size || '');
          var wideSize = sizeStr.indexOf('/') !== -1 || sizeStr.length > 6;

          return '<div class="ocg__panel-row' + (wideSize ? ' is-wide' : '') + '">' +
            '<div class="ocg__panel-size">' + self.esc(entry.size) + '</div>' +
            (skuOn ? '<div class="ocg__panel-sku">' + self.esc(entry.sku || '') + '</div>' : '') +
            '<div class="ocg__panel-stepwrap">' + stepper + '</div>' +
            '<span class="ocg__panel-line">' + formatMoney(entry.qty * entry.price, self.moneyFormat) + '</span>' +
            // Both breakpoints now — see the group ✕ note above for why 17c's desktop-only rule no
            // longer applies to the grouped panel.
            '<button type="button" class="ocg__panel-remove" data-oc-remove="' + self.esc(id) + '" ' +
              'title="' + self.esc(self.panelRemoveLabel) + '" ' +
              'aria-label="' + self.esc(self.panelRemoveLabel) + '">✕</button>' +
          '</div>';
        }).join('');

        return '<div class="ocg__panel-group">' + headRow + sizeRows + '</div>';
      }).join('');

      // 17e — the rows scroll; the footer stays OUTSIDE the scroll zone so the total never scrolls
      // away. The column header is gone with the flat layout: grouping carries the same information
      // in the header row, so a separate column head would label columns that no longer exist.
      //
      // <scroll-shadow> is Focal's own affordance element, adopted in the round-18 audit: it drives
      // the fade from a ResizeObserver AND a throttled scroll listener, where our hand-rolled
      // wireFade() listened to scroll only and so left the fade wrong after any viewport resize.
      // Its default paint is a dark radial gradient; the CSS overrides --scroll-shadow-bottom to
      // 17e's 36px WHITE fade, which is the whole reason the primitive is parameterised.
      // 🔴 THE COLUMN HEADER IS GONE FOR GOOD — 42a, CD-127. It was restored in the previous pass
      // on the reasoning that 41a's prose amendment « n'a pas révoqué les colonnes ». The reasoning
      // about prose amendments was sound; the conclusion was still an INFERENCE about an undrawn
      // composition, and it was wrong. CD has now drawn desktop and removed, explicitly: the column
      // header, the 2px column rule, the SKU column, the unit-price column, and « Total » as a foot
      // label. The only 2px rule that survives is the one under the panel TITLE.
      //
      // The lesson kept on the record: rejection of inference A is not evidence for inference B.
      // Both times the honest move was to flag the gap and wait for a frame — which is what the
      // third pass finally did, and what produced 42a.
      // 🔴 44a — PANEL v3: A BEIGE GROUND, EACH PRODUCT A WHITE BORDERED CARD. The 43d beige band
      // is DELETED. CD withdrew its own semantic objection in terms — « l'objection sémantique est
      // retirée du dossier : l'écho au panier est VOULU ; le badge "pas encore au panier" et le CTA
      // portent seuls l'état d'engagement, la surface n'a plus à le dire. »
      //
      // 🔴 THE FOOT IS NOW INSIDE THE GROUND AND SCROLLS WITH IT. It sat outside the scroll area in
      // 43d, and that is precisely what broke the reverted build: the foot stayed on the FLAT
      // panel's reference while the rows moved into cards, so the two money axes diverged. Both
      // references are now the same axis — totals stop 48 from the card edge, the foot 49 from the
      // ground (48 + the card's 1px border). Cross-checked from the panel edge:
      // 16 + 1 + 14 + 26 + 8 = 65 = 16 + 49. ✓
      //
      // The TITLE and its 2px rule stay OUT of the ground, on white — the ground begins under the
      // rule, and the ground is what the 58vh / min-240 cap applies to (43d §4).
      //
      // ⚠️ The cap goes on the element that ACTUALLY SCROLLS, and the cards are NOT flex children of
      // it. Both halves of that sentence are scar tissue: capping a parent and trusting flex to pass
      // the shrink down shrank the rows' container while the rows kept their own max-height and
      // painted over the footer; and groups made flex children of a capped parent shrank and
      // clipped. Block layout with margins for the gutter has neither failure mode.
      // 🔴 45d — THE FOOT IS FIXED AGAIN. 44a is AMENDED by CD, and the amendment is argued rather
      // than conceded: 44a buried the foot inside the scroll because a DIVERGENT MONEY AXIS had
      // broken the reverted build (a fixed foot on the flat panel's reference against cards on
      // theirs). 44a itself unified that axis — 48 in the card, 49 on the ground, one vertical — so
      // « ce risque n'existe plus par construction ». The reason fell away; the need reasserts
      // itself: « la barre porte le total COMBINÉ (sélection + panier) ; "qu'est-ce que ce panneau
      // vaut, lui" n'existe qu'au pied. »
      //
      // The foot stays ON THE GROUND (beige) but now sits OUTSIDE the scroller — a sibling of
      // <scroll-shadow>, not a last child of the rows. It carries the ground's own beige, the
      // card's bottom radius (the scroller gives its up), and 45d's NEW top filet
      // 1px rgba(53,53,53,.08): the cards scroll BENEATH it, the card's franc cut stays 17e's
      // scroll affordance, and the filet only says where the ground stops moving.
      var html =
        '<scroll-shadow class="ocg__panel-scroll">' +
          '<div class="ocg__panel-ground" data-oc-panel-rows>' +
            body +
          '</div>' +
        '</scroll-shadow>' +
        // 41a/1e — « N réfs · N articles » sits RIGHT, immediately before the total, at both
        // breakpoints; alone on the left it « read as detached ».
        '<div class="ocg__panel-foot" data-oc-panel-foot>' +
          '<span class="ocg__panel-count">' + this.esc(footerText) + '</span>' +
          '<span class="ocg__panel-total' + (footNull ? ' ocg__panel-total--none' : '') + '">' +
            this.esc(totalText) + '</span>' +
        '</div>';

      // 🔴 PRESERVE THE SCROLL POSITION across the re-render. Changing a quantity replaces the
      // panel's innerHTML, which destroys the scrolling element and resets the new one to 0 — so
      // adjusting a line halfway down the list threw the buyer back to the top, every time.
      // Read before the write, restore after: the row heights are unchanged by a quantity edit, so
      // the same offset lands on the same row.
      var scroller = this.querySelector('[data-oc-panel-rows]');
      var keepScroll = scroller ? scroller.scrollTop : 0;

      this.panelTableEl.innerHTML = html;

      if (keepScroll) {
        var restored = this.querySelector('[data-oc-panel-rows]');
        if (restored) restored.scrollTop = keepScroll;
      }

      // 🔴 Size AFTER the rows exist. onPanelToggled fires BEFORE recalc() has rendered anything,
      // so sizePanel ran against an empty table, bailed on its own guard and never wrote the cap —
      // leaving the panel correct only when 60vh happened to fit anyway, which is a coincidence
      // wearing a fix's clothes. Sizing here also re-runs whenever the content changes height (a
      // line removed, a product collapsed), which is exactly when a stale cap would show.
      if (this.panelOpen) this.sizePanel();
    }

    // ── Selection persistence (localStorage, no expiry) ─────────────────────────────────────────
    // Read defensively: storage can be unavailable (Safari private mode throws on access, not just
    // on write) and its contents are user-editable, so a malformed payload must degrade to an empty
    // basket rather than throwing on every page load.
    loadSelection() {
      try {
        var raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return {};
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        // Keep only entries that still carry the fields the panel and the bar read. A half-written
        // entry would render a row with no price and poison the totals.
        var clean = {};
        Object.keys(parsed).forEach(function (id) {
          var e = parsed[id];
          if (!e || typeof e !== 'object') return;
          var qty = parseInt(e.qty, 10);
          var price = parseInt(e.price, 10);
          if (!(qty > 0) || isNaN(price)) return;
          clean[id] = e;
        });
        return clean;
      } catch (err) {
        return {};
      }
    }

    saveSelection() {
      try {
        if (!Object.keys(this.selection).length) window.localStorage.removeItem(this.storageKey);
        else window.localStorage.setItem(this.storageKey, JSON.stringify(this.selection));
      } catch (err) {
        // Quota or private mode — the in-memory basket still works for this page. Never let a
        // storage failure break entry.
      }
      // 🔴 BROADCAST — the selection just changed, and the surfaces that REPORT it are not inside
      // this component. `.b2bsel` (40a in the drawer, 26c on the cart page) was re-rendered only by
      // a MutationObserver on `#mini-cart`, so it tracked CART mutations and nothing else: clearing
      // the selection in the sticky bar left the drawer still announcing it until a page load, and
      // the reverse. The basket is the source, so the basket announces its own change; the two
      // `ensure*` readers stay idempotent via their signature guard.
      document.dispatchEvent(new CustomEvent('waj:selection-changed'));
    }

    // Lucide `trash-2`, matching the icon family the theme already uses (the account icon is Lucide
    // `user`, 19px stroke 2 per 15c). Inline rather than an asset so it inherits currentColor and
    // needs no extra request; `currentColor` is what lets the pink hover ink it like the ✕.
    trashIcon() {
      return '<svg class="ocg__icon-trash" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
        'focusable="false" width="14" height="14">' +
        '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m3 0v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6"/>' +
        '<path d="M10 11v6M14 11v6"/></svg>';
    }

    // 🔴 Removal is ANIMATED, not instant. Operator: without a transition « it doesn't look like
    // said line item/group is getting deleted » — the row simply ceased to exist, which reads as a
    // glitch rather than as a deletion.
    //
    // The mechanic: mark the node, let it collapse, THEN mutate the basket and re-render. So the
    // basket is the source of truth exactly as before — this only defers the re-render by the
    // length of the animation. `_removing` guards against a second click during the collapse, and
    // the timeout is unconditional (never a transitionend — the panel can close mid-animation, and
    // that is precisely the event that never fires).
    animateRemoval(nodes, done) {
      var self = this;
      var els = nodes.filter(Boolean);
      if (!els.length || this._removing) { done(); return; }
      this._removing = true;
      els.forEach(function (el) {
        el.style.height = el.getBoundingClientRect().height + 'px';
        el.classList.add('is-removing');
      });
      // Force a frame so the collapse has a start value to animate FROM.
      void els[0].offsetHeight;
      els.forEach(function (el) { el.style.height = '0px'; });
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(function () { self._removing = false; done(); }, reduce ? 0 : 240);
    }

    // Retained only as the no-scroll-shadow fallback path; see renderPanel's note.
    wireFade() {
      var rows = this.querySelector('[data-oc-panel-rows]');
      var fade = this.querySelector('[data-oc-panel-fade]');
      if (!rows || !fade) return;
      var update = function () {
        var more = rows.scrollHeight - rows.scrollTop - rows.clientHeight > 1;
        fade.style.opacity = more ? '1' : '0';
      };
      rows.addEventListener('scroll', update);
      update();
    }

    // 19b, the mirror rule: the pad and the panel are two views of ONE selection line. The last
    // gesture wins and both show the same value — never a sum, never a conflict. So a change here
    // writes the basket AND the on-screen pad input, when that pad happens to be rendered.
    onPanelClick(event) {
      // 41a — the group ✕ removes every size of one product at once. Desktop-only, like the row ✕.
      // Handled before the per-line branch because a group button carries no variant id at all.
      var groupBtn = event.target.closest('[data-oc-remove-group]');
      if (groupBtn) {
        // 🔴 The group ✕ lives INSIDE the clickable product header, which 41a made a real <a> to the
        // order-pad. Without this the removal also followed the link and navigated to the PDP —
        // caught by the sweep, invisible in the diff. Any control nested in a link has to cancel the
        // link's default; being a <button> does not do it, because the anchor is the ancestor.
        event.preventDefault();
        event.stopPropagation();
        // Removing the group must not close the panel by emptying it mid-gesture — renderBar owns
        // that, and it runs from recalc() below.
        var key = groupBtn.getAttribute('data-oc-remove-group');
        var selection = this.selection;
        var self = this;
        // Collapse the whole group card first, then mutate — see animateRemoval.
        this.animateRemoval([groupBtn.closest('.ocg__panel-group')], function () {
          Object.keys(selection).forEach(function (vid) {
            var e = selection[vid];
            if ((e.productId || ('v' + vid)) !== key) return;
            delete selection[vid];
            self.mirrorToPad(vid, 0);
          });
          self.recalc();
        });
        return;
      }

      // Stepping is <quantity-selector>'s job now (see renderPanel) — the only clicks this handler
      // still owns are removals.
      var removeBtn = event.target.closest('[data-oc-remove]');
      if (!removeBtn) return;

      var id = removeBtn.getAttribute('data-oc-remove');
      if (!this.selection[id]) return;

      // The ✕ removes the whole size line in one gesture, at both breakpoints (operator ruling,
      // overriding 17c — the grouped panel freed the room 17c said did not exist).
      var self2 = this;
      var row = removeBtn.closest('.ocg__panel-row');
      // If this was the LAST size of its product, the header goes with it — otherwise the card
      // would collapse to a lone header with no rows for the length of the animation.
      var group = removeBtn.closest('.ocg__panel-group');
      var lastOfGroup = group && group.querySelectorAll('.ocg__panel-row').length === 1;
      this.animateRemoval([lastOfGroup ? group : row], function () {
        delete self2.selection[id];
        self2.mirrorToPad(id, 0);
        self2.recalc();
      });
    }

    // 19b's mirror rule, factored out so the row ✕, the group ✕ and a typed panel quantity all take
    // the same path. The pad may not be on screen: Focal rebuilds #facet-main on every filter/sort/
    // page change, so the line being changed can belong to a page that is no longer rendered —
    // which is precisely why the basket, not the DOM, is the source of truth.
    mirrorToPad(id, qty) {
      // Grid-scoped, and null on every page that has no grid — which is now most of them. Editing a
      // quantity in the panel from a PDP has no card to mirror it into, and must not reach for the
      // PDP pad's own row: that pad is a cart surface, not a selection one.
      var row = this.gridEl ? this.gridEl.querySelector('[data-op-row][data-variant-id="' + id + '"]') : null;
      if (!row) return;
      var input = row.querySelector('[data-op-qty]');
      if (!input) return;
      input.value = qty;
      var pad = row.closest('order-pad');
      if (pad && typeof pad.recalculate === 'function') pad.recalculate();
    }

    // The panel's stepper is now a real <input-number>, so a quantity can be TYPED there, not only
    // stepped. Without this the typed value would render and then be overwritten by the basket on
    // the next recalc — the input would silently refuse edits.
    onPanelInput(event) {
      var input = event.target.closest('[data-oc-qty]');
      if (!input) return;
      var id = input.getAttribute('data-oc-variant');
      var entry = this.selection[id];
      if (!entry) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      if (qty > 0) {
        entry.qty = qty;
        this.mirrorToPad(id, qty);
        this.recalc();
        return;
      }
      // Stepping to 0 IS a removal — including via the trash button the stepper shows at qty 1 —
      // so it gets the same collapse as the ✕ rather than blinking out.
      var self = this;
      var row = input.closest('.ocg__panel-row');
      var group = input.closest('.ocg__panel-group');
      var lastOfGroup = group && group.querySelectorAll('.ocg__panel-row').length === 1;
      this.animateRemoval([lastOfGroup ? group : row], function () {
        delete self.selection[id];
        self.mirrorToPad(id, 0);
        self.recalc();
      });
    }

    // 24c's « re-tap sur la grille » is now Focal's job. `.openable__overlay` — the 41a scrim —
    // covers the viewport whenever the panel is open, so every away-tap lands on it and
    // openable-element closes the panel itself. The lateral gutters beside the card fall through
    // because `.ocg__bar-panel` is pointer-events:none with the card re-enabling itself.
    //
    // What the removal of the old document-level handler also removes: it had to guard on
    // `event.target.isConnected`, because onPanelClick re-renders the panel's innerHTML and the
    // document listener then ran on a DETACHED node — contained by nothing, therefore "outside the
    // bar", so every stepper tap closed the panel. And it had to special-case `[aria-controls]` so
    // a click that OPENS the cart drawer was not read as an away-tap. Neither special case survives
    // the primitive, because there is no document-level listener left to confuse.

    // 🔴 THE THRESHOLD BASIS IS THE PRE-DISCOUNT SUBTOTAL — `original_total_price`, never
    // `total_price`. Every milestone qualifies on the subtotal BEFORE the privilege discount,
    // because the −10 % Function itself tests the pre-discount subtotal. Measuring the discounted
    // total makes the gauge chase a target that recedes as the buyer approaches it: measured in
    // production 2026-08-12, a cart of 902,70 € HT post-discount (1003,00 € before) was told
    // « encore 97,30 € pour la remise » — after the discount had already been earned.
    // Falls back to `total_price` only if the field is absent, which for a discountless cart is
    // the same number anyway.
    // Re-stamps BOTH numbers from a cart payload, so the money figure and the threshold basis can
    // never drift apart across the three paths that refresh them.
    applyCart(cart) {
      if (!cart) return;
      if (typeof cart.total_price === 'number') this.cartCents = cart.total_price;
      this.cartBasisCents = typeof cart.original_total_price === 'number'
        ? cart.original_total_price
        : this.cartCents;
    }

    // Keeps `cartCents` — which drives A1p, the cart context line and the gauge's projected total —
    // in step with the real cart, however the cart was changed.
    onCartChanged(event) {
      var self = this;
      var cart = event && event.detail && event.detail.cart;
      if (cart && typeof cart.total_price === 'number') {
        this.applyCart(cart);
        this.recalc();
        return;
      }
      // `cart:refresh` can arrive with no payload — the same fallback Focal's own CartCount uses.
      var root = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          if (c && typeof c.total_price === 'number') { self.applyCart(c); self.recalc(); }
        })
        .catch(function () { /* keep the last known figure rather than showing a wrong one */ });
    }

    // 41b/2a — the mobile whole-bar tap. Desktop keeps the pill as the only target: at 1440 the bar
    // spans the viewport and a full-width toggle zone would swallow ordinary clicks.
    onBarTap(event) {
      var desktop = this.desktopQuery ? this.desktopQuery.matches : window.innerWidth >= 1000;
      if (desktop) return;
      // « Afficher » is absent in A1 and A1p (26a — the panel is the SELECTION's surface and has
      // nothing to show). Where there is no affordance there must be no hidden target either.
      if (!this.toggleEl || this.toggleEl.hidden) return;
      // The toggle owns its own click; the CTA and retry are actions, not the toggle zone.
      if (event.target.closest('[data-oc-toggle], [data-oc-submit], [data-oc-retry]')) return;
      this.togglePanel();
    }

    onRetry() {
      if (this.errEl) this.errEl.hidden = true;
      this.onSubmit();
    }

    onSubmit() {
      var items = [];
      var labels = {};
      var self = this;
      // 🔴 Read the BASKET, not allSelected(): allSelected() projects to {variantId, qty, price}
      // and drops the name and size, which are exactly what the report has to print. Its shape is
      // left alone because other callers depend on it.
      var selection = this.selection;
      Object.keys(selection).forEach(function (id) {
        var entry = selection[id];
        // 43b — only what the cap allows. A capped line is not submitted at all rather than
        // submitted and clamped: the buyer is not told about a failure that was foreseeable.
        var qty = self.committableQty(id, entry);
        if (qty <= 0) return;
        items.push({ id: parseInt(id, 10), quantity: qty });
        // « {produit} — {taille} », captured BEFORE the selection is cleared, because the report
        // has to name lines that no longer exist in the basket by the time it renders.
        labels[parseInt(id, 10)] = entry.name + ' — ' + entry.size;
      });
      if (!items.length) return;

      this.setLoading(true);
      this.feedback('', null);
      savePartialReport(null);

      // Snapshot BEFORE the add, so a clamped line can be told apart from a line that simply was
      // already in the cart at that quantity.
      cartQuantitiesByVariant(this.cartUrl).then(function (before) {
        return fetch(self.addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items, sections: ['mini-cart'], sections_url: window.location.pathname })
        })
          .then(function (response) { return response.json().then(function (data) { return { ok: response.ok, data: data }; }); })
          .then(function (result) {
            if (result.ok) return self.finishAdd(result.data.sections);

            // 🔴 42c / CD-124 — a 422 IS NOT AN ERROR STATE ANY MORE.
            // Shopify has already added everything it could and clamped the rest. Read the cart to
            // find out exactly what landed, rather than trusting the status code or the message.
            return cartQuantitiesByVariant(self.cartUrl).then(function (after) {
              // 🔴 2026-08-07 — « nothing landed » IS NOT « genuine failure ». That premise was
              // shared by both earlier versions of this code and it is what let one dead size
              // destroy a whole selection: `/cart/add.js` rejects the ENTIRE payload when a line
              // cannot move at all (as opposed to merely asking for more than is available, which
              // it clamps). Re-send the lines singly before concluding anything — the same fix the
              // PDP pad now carries, and the case CD ruled on in 46f.
              if (after.total <= before.total) {
                return addItemsRecover(self.addUrl, self.cartUrl, items, true).then(function (res) {
                  if (!res.landed.length) {
                    /* 🔴 CASE B — nothing moved at all. 46f: « jamais de pastille réseau, jamais
                       de Réessayer : ici rien n'a échoué, le monde a changé. » So no state E, no
                       red bar, and never Shopify's raw description — the band on the panel says it,
                       the ceilings refresh, and the entry stays live for one more tap. */
                    /* 🔴 THE BAR SAYS IT IN CD's OWN PINK PILL (22d state E), not in a band.
                       Operator ruling 2026-08-07: « Should use pink error pill that had already
                       been provided from CD, with adapted wording, making it concise enough that
                       it fits pill design ». The pill is `.ocg__bar-err` — #FFD2D6, radius pill,
                       12px/600 — and it already exists; what it must NOT carry here is
                       « Réessayer », because 46f is explicit that retrying is the one action
                       guaranteed to fail again. `raceMode` hides it.

                       The pill takes the race TITLE verbatim — the same string the pad's band uses
                       as its title — so the two surfaces share a sentence rather than inventing
                       two. The pill carries no line list because it cannot; on this surface the
                       ROWS carry it, converting to RUPTURE or to the 43b tag in place. */
                    self.feedback('', null);
                    self.showError(true, self.raceLabel(), true);
                    self.setLoading(false);
                    self.markCeilingReached(items.map(function (it) { return it.id; }));
                    self.syncCart().then(function () { self.recalc(); });
                    return;
                  }
                  savePartialReport(res.refused.concat(res.deferred)
                    .map(function (id) { return labels[id]; }).filter(Boolean), 'cart');
                  // 🔴 A STOCK-REFUSED LINE NOW LEAVES THE SELECTION TOO (operator, 2026-08-29).
                  // 46f said « la saisie restante est intacte » and kept refused lines at their full
                  // quantity « so one tap re-sends what can now pass ». Operator, against a live
                  // screen: the refused line sits in the sticky selection after every other line has
                  // gone to the cart, which reads as work still to do. And 46f's premise does not
                  // hold: a line is refused here precisely because NO unit of it can move — it is
                  // SOLD OUT (the ceiling case clamps; measured 2026-08-30) — so the tap it is kept
                  // for is the one action guaranteed to fail again, which is 46f's own argument
                  // against « Réessayer » turned on itself. The band names it; that is the record.
                  //
                  // DEFERRED lines are the exception and stay: a thrown fetch is the network, not a
                  // stock verdict, so dropping those quantities would lose typed work to a blip.
                  var keepMap = {};
                  res.deferred.forEach(function (id) {
                    var entry = self.selection[id];
                    if (entry) keepMap[id] = entry.qty;
                  });
                  return self.finishAdd(null, keepMap);
                });
              }

              // Name every line that landed short of what was asked for, and — 46f — work out
              // what is still OWED on it. A clamped line is not a refused one: part of it landed,
              // so re-offering the whole quantity would ask for units already in the cart. The
              // shortfall is `expected − got`, and both terms are only in scope here.
              var clamped = [], keepMap = {};
              items.forEach(function (it) {
                var expected = (before.map[it.id] || 0) + it.quantity;
                var got = after.map[it.id] || 0;
                if (got < expected) {
                  clamped.push(labels[it.id]);
                  keepMap[it.id] = expected - got;
                }
              });
              savePartialReport(clamped.filter(Boolean), 'cart');

              // What deliberately does NOT happen: feedback(...) and showError(true). The bar says
              // NOTHING — « le chemin de succès normal ». 22d's chip E and « Réessayer » are now
              // reserved for the network/server failure below, where retrying means something; here
              // retrying is the one action guaranteed to fail again.
              //
              return self.finishAdd(null, keepMap);
            });
          });
      })
        .catch(function () {
          self.feedback((self.getAttribute('data-msg-network') || 'Une erreur réseau est survenue. Réessayez.'), 'error');
          self.showError(true);
          self.setLoading(false);
        });
    }

    // The shared success path — reached by the normal atomic add AND by a partial add, because CD
    // ruled a partial add IS a success. Keeping one path is the point: the two used to diverge, and
    // the divergence is what made a partial add feel like a failure.
    // 🔴 46f — `keepIds` / `cartAfter`: WHAT SURVIVES A PARTIAL ADD.
    // 42c cleared the whole selection on every add, partial included, so a buyer lost the very
    // lines the report had just named — the ones they still have to act on. D19 fixed that on the
    // PDP pad and explicitly left the panel for this pass.
    //   · refused (nothing landed for that id)  → the entry stays at its full quantity;
    //   · clamped (part landed)                 → the entry stays at the SHORTFALL, so it reads
    //     what is still owed rather than re-offering what is already in the cart.
    // Both quantities are computed by the CALLER, where the before- and after-cart snapshots are
    // both in scope; this method only re-seeds. Everything else clears exactly as before.
    finishAdd(sections, keepMap) {
      var self = this;
      var keepIds = keepMap ? Object.keys(keepMap) : [];
      var partial = keepIds.length > 0;
      return refreshCartAndOpen(self.cartUrl, sections)
            .then(function () {
              // Clear the basket FIRST — otherwise the next re-render would restore the quantities
              // that have just been added to the cart. The survivors are re-seeded from the copy
              // taken here, never from the DOM, because the pads are about to be reset too.
              var survivors = {};
              keepIds.forEach(function (id) {
                var entry = self.selection[id];
                var kept = keepMap[id];
                if (!entry || !(kept > 0)) return;   // a survivor of 0 is not a survivor
                survivors[id] = Object.assign({}, entry, { qty: kept });
              });
              self.selection = survivors;
              // And clear the PERSISTED copy in the same breath. Without this the selection would
              // come back on the next page load — the added lines resurrected beside the cart they
              // are already in, which is the two-baskets confusion the whole vocabulary exists to
              // prevent. (recalc() below also saves, but the clear must not depend on that.)
              self.saveSelection();
              self.pads.forEach(function (p) { p.reset(); });
              // 🔴 The « ✓ Ajouté au panier » line under the CTA is NOT shown any more.
              // (It was ours — `b2b_sticky_added` — not Focal's, despite appearing after the
              // drawer closed.) The add ALREADY opens the cart drawer, which is the confirmation:
              // the buyer has just seen their lines land in the cart. Repeating it under a CTA that
              // now reads « Sélection vide » above it says two contradictory things at once, and it
              // outlived the gesture by three seconds. The element and its setting stay in place so
              // a surface without a drawer (the PDP pad) can still use them.
              self.showError(false);
              // The selection has just BECOME the cart, so the bar's two halves both moved: the
              // panel has nothing left to show, and the gauge must keep measuring the same
              // projected total rather than appearing to drop back to zero. Re-read the cart.
              //
              // Clearing _panelWasOpen is load-bearing: the add OPENS the cart drawer, so the
              // overlay handler has already recorded "the panel was open" and would reopen it when
              // the drawer closes — onto an empty selection, which renderBar then closes again.
              // That open-then-immediately-close on a bar returning from display:none is what left
              // the sticky in a broken state.
              //
              // The panel closes on EVERY add, partial included — the band lives on the bar's
              // feedback line now, not on the panel card, so nothing is dismissed by closing it.
              // The survivors are still in the selection and the panel reopens onto them.
              self._panelWasOpen = false;
              self.setPanel(false);
              // 🔴 THE SURVIVORS ARE RE-CLAMPED AGAINST THE CART THAT JUST CHANGED, HERE.
              // An earlier version of this comment claimed the existing clamp pass would do it.
              // Measured 2026-08-07: it does NOT. That pass returns early on `cap === 0` (a cap-0
              // line becomes the 43b tag rather than being trimmed), so a survivor whose ceiling
              // has just fallen to 0 keeps its old number — inert, because `committableQty` still
              // reports 0, but stored and displayed as a quantity the buyer can never have. The
              // add is exactly the moment the ceiling moved, so the trim belongs on this line and
              // not in a pass written for a different trigger.
              self.syncCart().then(function () {
                if (partial) {
                  var trimmed = false;
                  Object.keys(self.selection).forEach(function (id) {
                    var entry = self.selection[id];
                    var cap = self.capFor(id, entry);
                    if (cap === null || entry.qty <= cap) return;
                    entry.qty = cap;              // cap 0 included: the row converts to the 43b tag
                    trimmed = true;
                  });
                  if (trimmed) self.saveSelection();
                }
                self.recalc();
              });
              self.setLoading(false);
              // The drawer has just been re-rendered by `cart:refresh`, so this is the moment its
              // list exists and is stable. Injecting earlier would write into markup Focal is about
              // to replace.
              self.showPartialBanner();
            })
            .catch(function () { window.location.href = self.cartUrl; });
    }

    // 🔴 46f — the band sits in the BAR's post-add feedback slot, below the CTA: the same place
    // « ✓ Ajouté au panier » renders. 42c put it at the head of the cart drawer's list.
    showPartialBanner() {
      // The observer is what actually guarantees this lands (see ensurePartialBanner); this call is
      // the immediate attempt for the case where Focal's re-render has already settled.
      ensurePartialBanner();
    }

    // 46f — the race sentence, read once off the same copy element the pad's band uses so the pill
    // and the band cannot drift into two vocabularies.
    raceLabel() {
      var el = document.querySelector('#mini-cart');
      return (el && el.getAttribute('data-race-title')) || '';
    }

    /* 🔴 Case B refreshes the ceilings on every card, exactly as the PDP pad does — same fork:
       a refused size the buyer has NONE of becomes RUPTURE, one she already holds becomes the 43b
       tag. Operator: « should be applied to plp as well ». */
    markCeilingReached(ids) {
      this.pads.forEach(function (pad) {
        if (typeof pad.markCeilingReached === 'function') pad.markCeilingReached(ids);
      });
    }

    // State E occupies the message + gauge slot, so the gauge steps aside while it shows (22d).
    // `message` overrides the pill's default copy; `noRetry` hides « Réessayer » — 46f reserves
    // retrying for the network, and on the race path it is guaranteed to fail again.
    showError(on, message, noRetry) {
      if (this.errEl) {
        var span = this.errEl.querySelector('span');
        if (span && message) span.textContent = message;
        var retry = this.errEl.querySelector('[data-oc-retry]');
        if (retry) retry.hidden = !!noRetry;
      }
      if (this.errEl) this.errEl.hidden = !on;
      if (this.gaugeEl) this.gaugeEl.hidden = !!on;
    }

    // The cart half of the projected total is only knowable server-side. Stamped into the markup at
    // render, and re-read here after an add so the bar does not sit on a stale figure.
    syncCart() {
      var self = this;
      return fetch(this.cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          if (!cart || typeof cart.total_price !== 'number') return;
          self.applyCart(cart);
          // Push to the header pill too. <b2b-cart-total> also listens to Focal's own cart events,
          // but this path does not depend on those events reaching the document — the add is our
          // own fetch, so we already hold the authoritative figure.
          document.querySelectorAll('b2b-cart-total').forEach(function (el) {
            if (typeof el.render === 'function') el.render(cart.total_price);
          });
        })
        .catch(function () { /* keep the last known figure rather than showing a wrong one */ });
    }

    // The CTA is Focal's `is="loader-button"` now (round-18 audit): setting aria-busy animates the
    // label out and a spinner in, with a promise so the end transition never races the start. We
    // previously toggled an `is-loading` class that had no spinner behind it at all — the button
    // simply sat there looking pressed. The class is kept in parallel so any CSS keyed to it still
    // applies, but aria-busy is what actually drives the transition.
    setLoading(state) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-loading', state);
      if (state) this.submitBtn.setAttribute('aria-busy', 'true');
      else this.submitBtn.removeAttribute('aria-busy');
      this.submitBtn.disabled = state || this.totalUnits === 0;
    }

    feedback(msg, kind) {
      if (!this.feedbackEl) return;
      this.feedbackEl.textContent = msg;
      this.feedbackEl.hidden = !msg;
      this.feedbackEl.classList.toggle('order-pad-collection__feedback--error', kind === 'error');
    }
  }

  customElements.define('order-pad-collection', OrderPadCollection);

  /* ── <b2b-cart-total> — the amount in the header cart pill ─────────────────────────────────────
   * Focal ships <cart-count>, which keeps the item COUNT live off its own cart:updated /
   * cart:refresh events. The B2B pill also prints the cart TOTAL (CD B2BHeader) and that was static
   * Liquid — so after an add the count moved and the amount sat stale until a reload.
   *
   * Built as a custom element on the theme's own events rather than patched into our add handler,
   * because the cart also changes from the mini-cart, the cart page and every other Focal surface,
   * all of which already fire these events. Theme-native beats a one-off.
   */
  class B2bCartTotal extends HTMLElement {
    connectedCallback() {
      this.format = this.getAttribute('data-money-format') || '${{amount}}';
      this._onCart = this._onCart.bind(this);
      document.addEventListener('cart:updated', this._onCart);
      document.addEventListener('cart:refresh', this._onCart);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCart);
      document.removeEventListener('cart:refresh', this._onCart);
    }

    _onCart(event) {
      var cart = event && event.detail && event.detail.cart;
      if (cart && typeof cart.total_price === 'number') {
        this.render(cart.total_price);
        return;
      }
      // cart:refresh can arrive with no payload — same fallback Focal's own CartCount uses.
      var self = this;
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          if (cart && typeof cart.total_price === 'number') self.render(cart.total_price);
        })
        .catch(function () { /* leave the last known figure rather than showing a wrong one */ });
    }

    render(cents) {
      this.textContent = formatMoney(cents, this.format);
    }
  }

  customElements.define('b2b-cart-total', B2bCartTotal);

  // Hide the quick-order sticky bar while ANY drawer/overlay is open (no stacked overlays).
  // Scroll-locking drawers (mini-cart, search, mobile-menu) are also covered purely in CSS via the
  // theme's .lock-all/.lock-mobile classes; this JS additionally covers the non-locking
  // cart-notification toast + names every overlay explicitly. Capture phase so it fires regardless
  // of bubbling.
  function isBlockingOverlay(el) {
    if (!el || !el.tagName) return false;
    var id = el.id || '';
    if (id === 'mini-cart' || id === 'search-drawer' || id === 'mobile-menu-drawer') return true;
    if (el.tagName === 'CART-NOTIFICATION') return true;
    if (el.classList && (el.classList.contains('mini-cart') || el.classList.contains('drawer'))) return true;
    return false;
  }
  /* CD 17d — opening the cart drawer takes the bar AND the panel out of frame; the selection is
   * neither emptied nor committed, and on closing the drawer the bar comes back "et le panneau si
   * ouvert". The bar is hidden by the CSS rules above, but the panel's OPEN state was not preserved:
   * it came back closed, and because the bar was display:none while the panel still held its
   * transform, the sticky returned mid-transition and the layout broke.
   * So: collapse the panel on open, remember that it was open, restore it on close. */
  function eachCollection(fn) {
    document.querySelectorAll('order-pad-collection').forEach(function (el) {
      if (typeof el.setPanel === 'function') fn(el);
    });
  }

  document.addEventListener('openable-element:open', function (e) {
    if (!isBlockingOverlay(e.target)) return;
    document.body.classList.add('qo-overlay-open');
    eachCollection(function (el) {
      el._panelWasOpen = el.panelOpen;
      if (el.panelOpen) { el.setPanel(false); el.recalc(); }
    });
  }, true);

  document.addEventListener('openable-element:close', function (e) {
    if (!isBlockingOverlay(e.target)) return;
    document.body.classList.remove('qo-overlay-open');
    eachCollection(function (el) {
      if (!el._panelWasOpen) return;
      el._panelWasOpen = false;
      // Next frame: the bar is coming back from display:none, and reopening in the same tick would
      // start the slide against a box that has not been laid out yet.
      requestAnimationFrame(function () { el.setPanel(true); el.recalc(); });
    });
  }, true);
})();

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE 390 LEGEND (CD 28-2) — tap the gauge, the status line becomes the milestone list for 4 s.

   🔴 CART PAGE AND DRAWER ONLY. The sticky bar is excluded at the source: its gauge never carries
   `data-b2bg-legend`, because 41b/2a already made the whole bar (bar the CTA) the tap target for
   expand/collapse and the operator ruled that stays (D94.3). Two behaviours cannot own one tap.

   DELEGATED ON `document`, deliberately. The drawer is re-rendered wholesale by the Section
   Rendering API on every cart change, so a listener bound to a gauge would die with the node it
   was bound to — the same class of failure as the tooltip that only one renderer knew about. One
   listener on the document outlives every re-render.

   Hover surfaces are excluded at runtime rather than by viewport width: the legend exists because
   there is no hover, so `hover: hover` is the honest test. A narrow desktop window is not a phone.
   ───────────────────────────────────────────────────────────────────────────────────────────── */
(function () {
  var LEGEND_MS = 4000;
  var timer = null;
  var openEl = null;

  function hide() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (openEl) { openEl.classList.remove('is-legend'); openEl = null; }
  }

  document.addEventListener('click', function (e) {
    var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    if (canHover) return;

    var gauge = e.target.closest ? e.target.closest('.b2bg[data-b2bg-legend]') : null;
    if (!gauge) { hide(); return; }

    // A second tap dismisses rather than restarting — the buyer has read it.
    if (openEl === gauge) { hide(); return; }

    hide();
    openEl = gauge;
    gauge.classList.add('is-legend');
    timer = setTimeout(hide, LEGEND_MS);
  }, false);

  // A re-render replaces the node we are holding, so drop the reference rather than leave a
  // detached element and a live timer behind it.
  document.addEventListener('cart:refresh', hide, false);
  document.addEventListener('cart:change', hide, false);
})();
